import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Users, Target, TrendingUp, Phone, Mail, MapPin, Calendar, FileText, DollarSign, Download, Car, Home, Building, Shield, Clock, AlertTriangle, CheckCircle, LogOut, User, Settings, Eye, EyeOff, UserPlus, RotateCcw, Bell, ListTodo, Zap, Calculator, PieChart, TrendingDown, Link, Webhook, Database, Upload, Activity, BarChart3, MessageSquare, Send, Inbox, Zap as BoltIcon, Settings as SettingsIcon, Users as UsersIcon, LayoutTemplate as Template, MessageCircle, Smartphone, Palette } from 'lucide-react';
import LeadForm from './components/LeadForm';
import UserForm from './components/UserForm';
import TaskForm from './components/TaskForm';
import QuoteForm from './components/QuoteForm';
import CommissionRuleForm from './components/CommissionRuleForm';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Switch } from './components/ui/switch';
import { Separator } from './components/ui/separator';
import { Alert, AlertDescription } from './components/ui/alert';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './components/ui/dropdown-menu';
import './App.css';
import './styles/themes.css';

// NEW: Import Integration Components
import { IntegrationsView } from './IntegrationsComponents';
// NEW: Import Communication Components
import { 
  CommunicationDashboard, 
  CommunicationTemplates, 
  CommunicationLogs, 
  CommunicationSettings 
} from './CommunicationsComponents';
// NEW: Import Analytics Components
import { AnalyticsView } from './AnalyticsComponents';
// NEW: Import Branding Configuration
import { COMPANY_CONFIG, THEME_CONFIG, APP_CONFIG, loadCustomBranding } from './config/branding';
import BrandingConfig from './components/BrandingConfig';
// NEW: Import Language Selector
import LanguageSelector from './components/LanguageSelector';
// NEW: Import Enhanced Authentication Handler
import AuthHandler from './components/AuthHandler';
import SidebarNavigation from './components/SidebarNavigation';
// NEW: Import Super Admin Components
import { 
  SuperAdminDashboard, 
  TenantManagement, 
  TenantForm, 
  WhiteLabelConfig,
  SystemUsersManagement 
} from './components/SuperAdminComponents';
// NEW: Import Landing Page Components
import LandingPage from './components/LandingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRODUCT_TYPES = ['Auto', 'Casa', 'Comercial'];//Will be translated
const LEAD_SOURCES = ['Web', 'WhatsApp', 'Meta Ads', 'Google Ads', 'Referencia', 'Llamada'];//Will be translated
const LEAD_STATUSES = ['Nuevo', 'Contactado', 'Cotizado', 'Cerrado', 'Perdido'];//Will be translated
const CARRIERS = ['Progressive', 'State Farm', 'Geico', 'Farmers', 'Liberty Mutual', 'Allstate', 'Otros'];//Will be translated
const QUOTE_STATUSES = ['Pendiente', 'Enviada', 'Aceptada', 'Rechazada', 'Vencida'];//Will be translated
const POLICY_STATUSES = ['Activa', 'Vencida', 'Cancelada', 'Renovada', 'Pendiente Renovación'];//Will be translated
const USER_ROLES = ['Admin', 'Manager', 'Agente', 'CSR', 'Auditor'];//Will be translated

// NEW: Renewal Engine Constants  
const RENEWAL_STATUSES = ['Pendiente', 'Procesando', 'Completada', 'Fallida', 'Cancelada'];//Will be translated
const TASK_STATUSES = ['Pendiente', 'En Progreso', 'Completada', 'Cancelada'];//Will be translated
const TASK_PRIORITIES = ['Baja', 'Media', 'Alta', 'Urgente'];//Will be translated
const NOTIFICATION_TYPES = ['Email', 'SMS', 'WhatsApp', 'Sistema'];//Will be translated

// NEW: Commission System Constants
const COMMISSION_TYPES = ['Percentage', 'Fixed Amount', 'Tiered', 'Bonus'];
const COMMISSION_CALCULATION_BASES = ['Annual Premium', 'First Year Premium', 'Policy Count', 'Total Volume'];
const COMMISSION_PERIOD_TYPES = ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual', 'Custom'];
const COMMISSION_STATUSES = ['Pending', 'Calculated', 'Under Review', 'Approved', 'Paid', 'Cancelled'];
const COMMISSION_PERIOD_STATUSES = ['Open', 'Calculating', 'Calculated', 'Under Review', 'Approved', 'Closed'];

// NEW: Integration System Constants
const INTEGRATION_TYPES = [
  'API REST', 'Webhook', 'File Upload', 'Facebook Ads', 'Google Ads', 
  'LinkedIn Ads', 'HubSpot', 'Mailchimp', 'Zapier', 'Custom'
];
const INTEGRATION_STATUSES = ['Active', 'Inactive', 'Error', 'Testing', 'Configured'];
const LEAD_PROCESSING_STATUSES = ['Pending', 'Processing', 'Completed', 'Failed', 'Duplicate', 'Invalid'];
const FIELD_MAPPING_TYPES = ['Direct', 'Computed', 'Conditional', 'Default'];
const WEBHOOK_EVENT_TYPES = ['lead.created', 'lead.updated', 'form.submitted', 'campaign.conversion', 'custom.event'];

// NEW: Communication Hub Constants
const COMMUNICATION_TYPES = ['Email', 'SMS', 'WhatsApp', 'Voice Call'];
const COMMUNICATION_STATUSES = ['Pending', 'Queued', 'Sent', 'Delivered', 'Failed', 'Bounced', 'Cancelled'];
const TEMPLATE_TYPES = [
  'Lead Welcome', 'Quote Generated', 'Quote Reminder', 'Policy Confirmation',
  'Renewal Reminder', 'Renewal Urgent', 'Task Assignment', 'Task Reminder',
  'Task Completed', 'Commission Report', 'Custom'
];
const MESSAGE_PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'];
const AUTOMATION_TRIGGERS = [
  'Lead Created', 'Quote Generated', 'Policy Created', 'Renewal Due',
  'Task Assigned', 'Custom Date', 'Manual'
];

const STATUS_COLORS = {
  'Nuevo': 'bg-blue-100 text-blue-800 border-blue-200',
  'Contactado': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Cotizado': 'bg-purple-100 text-purple-800 border-purple-200',
  'Cerrado': 'bg-green-100 text-green-800 border-green-200',
  'Perdido': 'bg-red-100 text-red-800 border-red-200'
};

const QUOTE_STATUS_COLORS = {
  'Pendiente': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Enviada': 'bg-blue-100 text-blue-800 border-blue-200',
  'Aceptada': 'bg-green-100 text-green-800 border-green-200',
  'Rechazada': 'bg-red-100 text-red-800 border-red-200',
  'Vencida': 'bg-orange-100 text-orange-800 border-orange-200'
};

const POLICY_STATUS_COLORS = {
  'Activa': 'bg-green-100 text-green-800 border-green-200',
  'Vencida': 'bg-red-100 text-red-800 border-red-200',
  'Cancelada': 'bg-gray-100 text-gray-800 border-gray-200',
  'Renovada': 'bg-blue-100 text-blue-800 border-blue-200',
  'Pendiente Renovación': 'bg-yellow-100 text-yellow-800 border-yellow-200'
};

// NEW: Renewal Engine Status Colors
const RENEWAL_STATUS_COLORS = {
  'Pendiente': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Procesando': 'bg-blue-100 text-blue-800 border-blue-200',
  'Completada': 'bg-green-100 text-green-800 border-green-200',
  'Fallida': 'bg-red-100 text-red-800 border-red-200',
  'Cancelada': 'bg-gray-100 text-gray-800 border-gray-200'
};

const TASK_STATUS_COLORS = {
  'Pendiente': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'En Progreso': 'bg-blue-100 text-blue-800 border-blue-200',
  'Completada': 'bg-green-100 text-green-800 border-green-200',
  'Cancelada': 'bg-gray-100 text-gray-800 border-gray-200'
};

const TASK_PRIORITY_COLORS = {
  'Baja': 'bg-gray-100 text-gray-800 border-gray-200',
  'Media': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Alta': 'bg-orange-100 text-orange-800 border-orange-200',
  'Urgente': 'bg-red-100 text-red-800 border-red-200'
};

// NEW: Commission System Status Colors
const COMMISSION_STATUS_COLORS = {
  'Pending': 'bg-gray-100 text-gray-800 border-gray-200',
  'Calculated': 'bg-blue-100 text-blue-800 border-blue-200',
  'Under Review': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Approved': 'bg-green-100 text-green-800 border-green-200',
  'Paid': 'bg-green-500 text-white border-green-500',
  'Cancelled': 'bg-red-100 text-red-800 border-red-200'
};

const COMMISSION_PERIOD_STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-800 border-blue-200',
  'Calculating': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Calculated': 'bg-orange-100 text-orange-800 border-orange-200',
  'Under Review': 'bg-purple-100 text-purple-800 border-purple-200',
  'Approved': 'bg-green-100 text-green-800 border-green-200',
  'Closed': 'bg-gray-100 text-gray-800 border-gray-200'
};

// NEW: Integration System Status Colors
const INTEGRATION_STATUS_COLORS = {
  'Active': 'bg-green-100 text-green-800 border-green-200',
  'Inactive': 'bg-gray-100 text-gray-800 border-gray-200',
  'Error': 'bg-red-100 text-red-800 border-red-200',
  'Testing': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Configured': 'bg-blue-100 text-blue-800 border-blue-200'
};

const LEAD_PROCESSING_STATUS_COLORS = {
  'Pending': 'bg-gray-100 text-gray-800 border-gray-200',
  'Processing': 'bg-blue-100 text-blue-800 border-blue-200',
  'Completed': 'bg-green-100 text-green-800 border-green-200',
  'Failed': 'bg-red-100 text-red-800 border-red-200',
  'Duplicate': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Invalid': 'bg-orange-100 text-orange-800 border-orange-200'
};

const ROLE_COLORS = {
  'Admin': 'bg-red-100 text-red-800 border-red-200',
  'Manager': 'bg-purple-100 text-purple-800 border-purple-200',
  'Agente': 'bg-blue-100 text-blue-800 border-blue-200',
  'CSR': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Auditor': 'bg-gray-100 text-gray-800 border-gray-200'
};

// NEW: Communication Hub Status Colors
const COMMUNICATION_STATUS_COLORS = {
  'Pending': 'bg-gray-100 text-gray-800 border-gray-200',
  'Queued': 'bg-blue-100 text-blue-800 border-blue-200',
  'Sent': 'bg-green-100 text-green-800 border-green-200',
  'Delivered': 'bg-green-100 text-green-800 border-green-200',
  'Failed': 'bg-red-100 text-red-800 border-red-200',
  'Bounced': 'bg-orange-100 text-orange-800 border-orange-200',
  'Cancelled': 'bg-gray-100 text-gray-800 border-gray-200'
};

const MESSAGE_PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-800 border-gray-200',
  'Normal': 'bg-blue-100 text-blue-800 border-blue-200',
  'High': 'bg-orange-100 text-orange-800 border-orange-200',
  'Urgent': 'bg-red-100 text-red-800 border-red-200'
};

// Set up axios interceptor for authentication
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

function App() {
  const { t } = useTranslation();
  
  // Helper functions to get translated values for constants
  const getTranslatedProductTypes = () => [
    t('productTypes.auto'), t('productTypes.home'), t('productTypes.commercial')
  ];
  
  const getTranslatedLeadSources = () => [
    t('leads.sourceOptions.web'), t('leads.sourceOptions.whatsapp'), 
    t('leads.sourceOptions.metaAds'), t('leads.sourceOptions.googleAds'), 
    t('leads.sourceOptions.referral'), t('leads.sourceOptions.call')
  ];
  
  const getTranslatedLeadStatuses = () => [
    t('leads.statusOptions.new'), t('leads.statusOptions.contacted'), 
    t('leads.statusOptions.quoted'), t('leads.statusOptions.closed'), 
    t('leads.statusOptions.lost')
  ];
  
  const getTranslatedCarriers = () => [
    t('carriers.progressive'), t('carriers.stateFarm'), t('carriers.geico'), 
    t('carriers.farmers'), t('carriers.libertyMutual'), t('carriers.allstate'), 
    t('carriers.others')
  ];
  
  const getTranslatedQuoteStatuses = () => [
    t('quotes.statusOptions.pending'), t('quotes.statusOptions.sent'), 
    t('quotes.statusOptions.accepted'), t('quotes.statusOptions.rejected'), 
    t('quotes.statusOptions.expired')
  ];
  
  const getTranslatedPolicyStatuses = () => [
    t('policies.statusOptions.active'), t('policies.statusOptions.expired'), 
    t('policies.statusOptions.cancelled'), t('policies.statusOptions.renewed'), 
    t('policies.statusOptions.pendingRenewal')
  ];
  
  const getTranslatedUserRoles = () => [
    t('users.roleOptions.admin'), t('users.roleOptions.manager'), 
    t('users.roleOptions.agent'), t('users.roleOptions.csr'), 
    t('users.roleOptions.auditor')
  ];

  // Helper functions to translate status values from backend
  const translateLeadStatus = (status) => {
    const statusMap = {
      'Nuevo': t('leads.statusOptions.new'),
      'Contactado': t('leads.statusOptions.contacted'),
      'Cotizado': t('leads.statusOptions.quoted'),
      'Cerrado': t('leads.statusOptions.closed'),
      'Perdido': t('leads.statusOptions.lost')
    };
    return statusMap[status] || status;
  };

  const translateQuoteStatus = (status) => {
    const statusMap = {
      'Pendiente': t('quotes.statusOptions.pending'),
      'Enviada': t('quotes.statusOptions.sent'),
      'Aceptada': t('quotes.statusOptions.accepted'),
      'Aceptadas': t('quotes.statusOptions.accepted'), // Handle plural
      'Rechazada': t('quotes.statusOptions.rejected'),
      'Vencida': t('quotes.statusOptions.expired'),
      'Borrador': t('quotes.statusOptions.pending') // Map Borrador to pending
    };
    return statusMap[status] || status;
  };

  const translatePolicyStatus = (status) => {
    const statusMap = {
      'Activa': t('policies.statusOptions.active'),
      'Vencida': t('policies.statusOptions.expired'),
      'Cancelada': t('policies.statusOptions.cancelled'),
      'Renovada': t('policies.statusOptions.renewed'),
      'Pendiente Renovación': t('policies.statusOptions.pendingRenewal')
    };
    return statusMap[status] || status;
  };

  const translateUserRole = (role) => {
    const roleMap = {
      'Admin': t('users.roleOptions.admin'),
      'Manager': t('users.roleOptions.manager'),
      'Agente': t('users.roleOptions.agent'),
      'CSR': t('users.roleOptions.csr'),
      'Auditor': t('users.roleOptions.auditor')
    };
    return roleMap[role] || role;
  };

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [leads, setLeads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [users, setUsers] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedQuoteLeadId, setSelectedQuoteLeadId] = useState(null); // For QuoteForm
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);

  // NEW: Renewal Engine State
  const [renewals, setRenewals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [renewalConfig, setRenewalConfig] = useState(null);
  const [renewalEngineStatus, setRenewalEngineStatus] = useState(null);
  const [showRenewalConfigDialog, setShowRenewalConfigDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [selectedRenewal, setSelectedRenewal] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // NEW: Commission System State
  const [commissions, setCommissions] = useState([]);
  const [commissionSchemes, setCommissionSchemes] = useState([]);
  const [commissionRules, setCommissionRules] = useState([]);
  const [commissionPeriods, setCommissionPeriods] = useState([]);
  const [commissionDashboard, setCommissionDashboard] = useState(null);
  const [showCommissionSchemeDialog, setShowCommissionSchemeDialog] = useState(false);
  const [showCommissionRuleDialog, setShowCommissionRuleDialog] = useState(false);
  const [showCommissionPeriodDialog, setShowCommissionPeriodDialog] = useState(false);
  const [showCommissionAdjustmentDialog, setShowCommissionAdjustmentDialog] = useState(false);
  const [selectedCommissionScheme, setSelectedCommissionScheme] = useState(null);
  const [selectedCommissionPeriod, setSelectedCommissionPeriod] = useState(null);
  const [selectedCommission, setSelectedCommission] = useState(null);

  // NEW: Integration System State
  const [integrations, setIntegrations] = useState([]);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [incomingLeads, setIncomingLeads] = useState([]);
  const [integrationDashboard, setIntegrationDashboard] = useState(null);
  const [showIntegrationDialog, setShowIntegrationDialog] = useState(false);
  const [showFieldMappingDialog, setShowFieldMappingDialog] = useState(false);
  const [showTestIntegrationDialog, setShowTestIntegrationDialog] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  // NEW: Communication Hub State
  const [communicationTemplates, setCommunicationTemplates] = useState([]);
  const [communicationLogs, setCommunicationLogs] = useState([]);
  const [communicationAutomations, setCommunicationAutomations] = useState([]);
  const [communicationSettings, setCommunicationSettings] = useState(null);
  const [communicationStats, setCommunicationStats] = useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showAutomationDialog, setShowAutomationDialog] = useState(false);
  const [showSendCommunicationDialog, setShowSendCommunicationDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showTestCommunicationDialog, setShowTestCommunicationDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [activeCommunicationTab, setActiveCommunicationTab] = useState('dashboard');

  // NEW: Branding Configuration State
  const [showBrandingConfig, setShowBrandingConfig] = useState(false);

  // NEW: Super Admin State
  const [superAdminDashboard, setSuperAdminDashboard] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [allSystemUsers, setAllSystemUsers] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantBrandingConfig, setTenantBrandingConfig] = useState(null);
  const [showTenantDialog, setShowTenantDialog] = useState(false);
  const [showTenantBrandingDialog, setShowTenantBrandingDialog] = useState(false);
  const [activeSuperAdminTab, setActiveSuperAdminTab] = useState('dashboard');
  const [tenantFilters, setTenantFilters] = useState({
    search: '',
    status: 'all',
    plan: 'all'
  });
  const [userFilters, setUserFilters] = useState({
    search: '',
    role: 'all',
    tenant: 'all'
  });

  // NEW: Sidebar State
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // NEW: Landing Page State
  const [showLandingPage, setShowLandingPage] = useState(!isAuthenticated);
  const [currentPage, setCurrentPage] = useState('landing'); // 'landing', 'privacy', 'terms'

  // Synchronize landing page visibility with authentication state
  useEffect(() => {
    setShowLandingPage(!isAuthenticated);
  }, [isAuthenticated]);

  // NEW: Analytics & Reporting State
  const [analyticsDashboard, setAnalyticsDashboard] = useState(null);
  const [timeBasedMetrics, setTimeBasedMetrics] = useState({});
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [conversionFunnel, setConversionFunnel] = useState(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [analyticsDateRange, setAnalyticsDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [selectedPeriod, setSelectedPeriod] = useState('Monthly');
  const [analyticsFilters, setAnalyticsFilters] = useState({
    agents: [],
    products: [],
    sources: [],
    carriers: []
  });
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);

  // Authentication state - cleaned up, handled by AuthHandler
  const [authError, setAuthError] = useState('');

  // Old stable callback functions removed - now handled by AuthHandler

  // Lead form controlled tab state - CLEANED UP - NOW HANDLED BY FORM COMPONENTS

  // User form stable callbacks - CLEANED UP - NOW HANDLED BY UserForm COMPONENT

  // Task form stable callbacks - CLEANED UP - NOW HANDLED BY TaskForm COMPONENT

  // Quote and Commission form callbacks - CLEANED UP - NOW HANDLED BY REACT HOOK FORM COMPONENTS

  // Commission scheme stable callbacks
  const handleCommissionSchemeFieldChange = useCallback((field, value) => {
    setNewCommissionScheme(prev => ({ ...prev, [field]: value }));
  }, []);

  // Commission period stable callbacks
  const handleCommissionPeriodFieldChange = useCallback((field, value) => {
    setNewCommissionPeriod(prev => ({ ...prev, [field]: value }));
  }, []);

  // Commission adjustment stable callbacks
  const handleCommissionAdjustmentFieldChange = useCallback((field, value) => {
    setCommissionAdjustment(prev => ({ ...prev, [field]: value }));
  }, []);

  // Integration stable callbacks
  const handleIntegrationFieldChange = useCallback((field, value) => {
    setNewIntegration(prev => ({ ...prev, [field]: value }));
  }, []);

  // Field mapping stable callbacks
  const handleFieldMappingFieldChange = useCallback((field, value) => {
    setNewFieldMapping(prev => ({ ...prev, [field]: value }));
  }, []);

  // User management state
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: '',
    full_name: '',
    phone: '',
    department: ''
  });

  // Profile update state
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    department: ''
  });

  // Form state - NOW HANDLED BY FORM COMPONENTS WITH REACT HOOK FORM

  // NEW: Form state for tasks
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'Media',
    due_date: '',
    renewal_id: '',
    policy_id: '',
    lead_id: ''
  });

  // NEW: Form state for renewal config
  const [configUpdate, setConfigUpdate] = useState({
    days_before_expiration: 30,
    auto_generate_quotes: true,
    auto_send_notifications: true,
    auto_create_tasks: true,
    notification_types: ['Email', 'Sistema']
  });

  // NEW: Commission System Form States - CLEANED UP - USING REACT HOOK FORM
  const [newCommissionScheme, setNewCommissionScheme] = useState({
    name: '',
    description: '',
    applies_to_roles: [],
    applies_to_products: [],
    period_type: 'Monthly'
  });

  // Commission Rule Form - NOW HANDLED BY CommissionRuleForm COMPONENT

  const [newCommissionPeriod, setNewCommissionPeriod] = useState({
    name: '',
    period_type: 'Monthly',
    start_date: '',
    end_date: '',
    cut_off_date: '',
    scheme_id: '',
    notes: ''
  });

  const [commissionAdjustment, setCommissionAdjustment] = useState({
    commission_id: '',
    adjustment_amount: 0,
    reason: '',
    adjustment_type: 'Manual'
  });

  // NEW: Integration System Form States
  const [newIntegration, setNewIntegration] = useState({
    name: '',
    description: '',
    integration_type: 'Webhook',
    api_endpoint: '',
    api_key: '',
    api_secret: '',
    webhook_url: '',
    webhook_secret: '',
    settings: {},
    headers: {},
    auto_process: true,
    duplicate_check: true,
    validate_data: true
  });

  const [newFieldMapping, setNewFieldMapping] = useState({
    integration_id: '',
    source_field: '',
    target_field: '',
    mapping_type: 'Direct',
    transformation_rule: '',
    condition: '',
    default_value: '',
    required: false,
    validation_rule: ''
  });

  const [testIntegrationData, setTestIntegrationData] = useState({
    integration_id: '',
    test_data: {}
  });

  // Form state - CLEANED UP - USING REACT HOOK FORM COMPONENTS

  // Form state for policies
  const [newPolicy, setNewPolicy] = useState({
    quote_id: '',
    effective_date: '',
    expiration_date: '',
    agent_assigned: '',
    notes: ''
  });

  useEffect(() => {
    // Load custom branding configuration
    loadCustomBranding();
    
    // Check for existing authentication
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardStats();
      if (hasPermission(['Admin', 'Manager', 'Agente', 'CSR'])) {
        fetchLeads();
        fetchQuotes();
        fetchPolicies();
        // NEW: Fetch renewal engine data
        fetchRenewals();
        fetchTasks();
        fetchNotifications();
        fetchRenewalEngineStatus();
      }
      if (hasPermission(['Admin', 'Manager', 'Agente'])) {
        // NEW: Fetch commission data
        fetchCommissions();
        fetchCommissionSchemes();
        fetchCommissionPeriods();
        fetchCommissionDashboard();
      }
      if (hasPermission(['Admin', 'Manager'])) {
        fetchUsers();
        // NEW: Fetch integration data
        fetchIntegrations();
        fetchIncomingLeads();
        fetchIntegrationDashboard();
        // NEW: Fetch communication data
        fetchCommunicationTemplates();
        fetchCommunicationLogs();
        fetchCommunicationAutomations();
        fetchCommunicationStats();
        // NEW: Fetch analytics data
        fetchAnalyticsDashboard();
        fetchAgentPerformance();
        fetchConversionFunnel();
        fetchRevenueAnalytics();
      }
      if (hasPermission(['Admin'])) {
        fetchRenewalConfig();
        fetchCommunicationSettings(); // Admin only
      }
    }
  }, [isAuthenticated, currentUser]);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    
    if (token && userData) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setCurrentUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  };

  const hasPermission = (allowedRoles) => {
    return currentUser && allowedRoles.includes(currentUser.role);
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    setCurrentUser(null);
    setIsAuthenticated(false);
    setActiveView('dashboard');
    setShowLandingPage(true);
    setCurrentPage('landing');
  };

  // NEW: Landing Page Navigation Functions
  const handleLoginClick = () => {
    setShowLandingPage(false);
    setCurrentPage('login');
  };

  const handleSignUpClick = () => {
    setShowLandingPage(false);
    setCurrentPage('signup');
  };

  const handleBackToLanding = () => {
    setShowLandingPage(true);
    setCurrentPage('landing');
  };

  const handleShowPrivacyPolicy = () => {
    setCurrentPage('privacy');
  };

  const handleShowTermsOfService = () => {
    setCurrentPage('terms');
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${API}/policies`);
      setPolicies(response.data);
    } catch (error) {
      console.error('Error fetching policies:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // NEW: Renewal Engine Fetch Functions
  const fetchRenewals = async () => {
    try {
      const response = await axios.get(`${API}/renewals`);
      setRenewals(response.data);
    } catch (error) {
      console.error('Error fetching renewals:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchRenewalConfig = async () => {
    try {
      const response = await axios.get(`${API}/renewals/config`);
      setRenewalConfig(response.data);
      setConfigUpdate(response.data);
    } catch (error) {
      console.error('Error fetching renewal config:', error);
    }
  };

  const fetchRenewalEngineStatus = async () => {
    try {
      const response = await axios.get(`${API}/renewals/engine-status`);
      setRenewalEngineStatus(response.data);
    } catch (error) {
      console.error('Error fetching renewal engine status:', error);
    }
  };

  // NEW: Commission System Fetch Functions
  const fetchCommissions = async () => {
    try {
      const response = await axios.get(`${API}/commissions`);
      setCommissions(response.data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
    }
  };

  const fetchCommissionSchemes = async () => {
    try {
      const response = await axios.get(`${API}/commissions/schemes`);
      setCommissionSchemes(response.data);
    } catch (error) {
      console.error('Error fetching commission schemes:', error);
    }
  };

  const fetchCommissionRules = async (schemeId) => {
    try {
      const response = await axios.get(`${API}/commissions/schemes/${schemeId}/rules`);
      setCommissionRules(response.data);
    } catch (error) {
      console.error('Error fetching commission rules:', error);
    }
  };

  const fetchCommissionPeriods = async () => {
    try {
      const response = await axios.get(`${API}/commissions/periods`);
      setCommissionPeriods(response.data);
    } catch (error) {
      console.error('Error fetching commission periods:', error);
    }
  };

  const fetchCommissionDashboard = async () => {
    try {
      const response = await axios.get(`${API}/commissions/dashboard`);
      setCommissionDashboard(response.data);
    } catch (error) {
      console.error('Error fetching commission dashboard:', error);
    }
  };

  // NEW: Integration System Fetch Functions
  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations`);
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const fetchFieldMappings = async (integrationId) => {
    try {
      const response = await axios.get(`${API}/integrations/${integrationId}/field-mappings`);
      setFieldMappings(response.data);
    } catch (error) {
      console.error('Error fetching field mappings:', error);
    }
  };

  const fetchIncomingLeads = async () => {
    try {
      const response = await axios.get(`${API}/incoming-leads`);
      setIncomingLeads(response.data);
    } catch (error) {
      console.error('Error fetching incoming leads:', error);
    }
  };

  const fetchIntegrationDashboard = async () => {
    try {
      const response = await axios.get(`${API}/integrations/dashboard`);
      setIntegrationDashboard(response.data);
    } catch (error) {
      console.error('Error fetching integration dashboard:', error);
    }
  };

  // NEW: Communication Hub Fetch Functions
  const fetchCommunicationTemplates = async () => {
    try {
      const response = await axios.get(`${API}/communications/templates`);
      setCommunicationTemplates(response.data);
    } catch (error) {
      console.error('Error fetching communication templates:', error);
    }
  };

  const fetchCommunicationLogs = async () => {
    try {
      const response = await axios.get(`${API}/communications/logs`);
      setCommunicationLogs(response.data);
    } catch (error) {
      console.error('Error fetching communication logs:', error);
    }
  };

  const fetchCommunicationAutomations = async () => {
    try {
      const response = await axios.get(`${API}/communications/automations`);
      setCommunicationAutomations(response.data);
    } catch (error) {
      console.error('Error fetching communication automations:', error);
    }
  };

  const fetchCommunicationSettings = async () => {
    try {
      const response = await axios.get(`${API}/communications/settings`);
      setCommunicationSettings(response.data);
    } catch (error) {
      console.error('Error fetching communication settings:', error);
    }
  };

  const fetchCommunicationStats = async () => {
    try {
      const response = await axios.get(`${API}/communications/stats`);
      setCommunicationStats(response.data);
    } catch (error) {
      console.error('Error fetching communication stats:', error);
    }
  };

  const createCommunicationTemplate = async (templateData) => {
    try {
      const response = await axios.post(`${API}/communications/templates`, templateData);
      fetchCommunicationTemplates();
      setShowTemplateDialog(false);
      return response.data;
    } catch (error) {
      console.error('Error creating communication template:', error);
      throw error;
    }
  };

  const updateCommunicationTemplate = async (templateId, templateData) => {
    try {
      const response = await axios.put(`${API}/communications/templates/${templateId}`, templateData);
      fetchCommunicationTemplates();
      setShowTemplateDialog(false);
      return response.data;
    } catch (error) {
      console.error('Error updating communication template:', error);
      throw error;
    }
  };

  const deleteCommunicationTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/communications/templates/${templateId}`);
      fetchCommunicationTemplates();
    } catch (error) {
      console.error('Error deleting communication template:', error);
    }
  };

  const createCommunicationAutomation = async (automationData) => {
    try {
      const response = await axios.post(`${API}/communications/automations`, automationData);
      fetchCommunicationAutomations();
      setShowAutomationDialog(false);
      return response.data;
    } catch (error) {
      console.error('Error creating communication automation:', error);
      throw error;
    }
  };

  const updateCommunicationAutomation = async (automationId, automationData) => {
    try {
      const response = await axios.put(`${API}/communications/automations/${automationId}`, automationData);
      fetchCommunicationAutomations();
      setShowAutomationDialog(false);
      return response.data;
    } catch (error) {
      console.error('Error updating communication automation:', error);
      throw error;
    }
  };

  const sendCommunication = async (communicationData) => {
    try {
      const response = await axios.post(`${API}/communications/send`, communicationData);
      fetchCommunicationLogs();
      fetchCommunicationStats();
      setShowSendCommunicationDialog(false);
      return response.data;
    } catch (error) {
      console.error('Error sending communication:', error);
      throw error;
    }
  };

  const testCommunication = async (testData) => {
    try {
      const response = await axios.post(`${API}/communications/test`, testData);
      return response.data;
    } catch (error) {
      console.error('Error testing communication:', error);
      throw error;
    }
  };

  const updateCommunicationSettings = async (settingsData) => {
    try {
      const response = await axios.put(`${API}/communications/settings`, settingsData);
      setCommunicationSettings(response.data);
      setShowSettingsDialog(false);
      return response.data;
    } catch (error) {
      console.error('Error updating communication settings:', error);
      throw error;
    }
  };

  // NEW: Super Admin API Functions
  const fetchSuperAdminDashboard = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/dashboard`);
      setSuperAdminDashboard(response.data);
    } catch (error) {
      console.error('Error fetching super admin dashboard:', error);
    }
  };

  const fetchTenants = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.plan && filters.plan !== 'all') params.append('plan', filters.plan);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/tenants?${params}`);
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchAllSystemUsers = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.tenant && filters.tenant !== 'all') {
        if (filters.tenant === 'system') {
          params.append('tenant_id', '');
        } else {
          params.append('tenant_id', filters.tenant);
        }
      }
      if (filters.role && filters.role !== 'all') params.append('role', filters.role);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/super-admin/users?${params}`);
      setAllSystemUsers(response.data);
    } catch (error) {
      console.error('Error fetching all system users:', error);
    }
  };

  const createTenant = async (tenantData) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/tenants`, tenantData);
      setShowTenantDialog(false);
      await fetchTenants(tenantFilters);
      alert('Tenant created successfully!');
      return response.data;
    } catch (error) {
      console.error('Error creating tenant:', error);
      alert(error.response?.data?.detail || 'Error creating tenant');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateTenant = async (tenantId, tenantData) => {
    try {
      setLoading(true);
      const response = await axios.put(`${API}/tenants/${tenantId}`, tenantData);
      setShowTenantDialog(false);
      setSelectedTenant(null);
      await fetchTenants(tenantFilters);
      alert('Tenant updated successfully!');
      return response.data;
    } catch (error) {
      console.error('Error updating tenant:', error);
      alert(error.response?.data?.detail || 'Error updating tenant');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this tenant? This action cannot be undone and will delete all associated data.')) {
      return;
    }
    
    try {
      setLoading(true);
      await axios.delete(`${API}/tenants/${tenantId}`);
      await fetchTenants(tenantFilters);
      alert('Tenant deleted successfully!');
    } catch (error) {
      console.error('Error deleting tenant:', error);
      alert(error.response?.data?.detail || 'Error deleting tenant');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantBranding = async (tenantId) => {
    try {
      const response = await axios.get(`${API}/tenants/${tenantId}/branding`);
      setTenantBrandingConfig(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant branding:', error);
      // Create default branding if none exists
      const defaultBranding = {
        tenant_id: tenantId,
        company_name: selectedTenant?.name || '',
        primary_color: '#006400',
        accent_color: '#FFD700',
        background_color: '#f8fafc',
        text_color: '#1f2937'
      };
      setTenantBrandingConfig(defaultBranding);
      return defaultBranding;
    }
  };

  const updateTenantBranding = async (tenantId, brandingData) => {
    try {
      setLoading(true);
      const response = await axios.put(`${API}/tenants/${tenantId}/branding`, brandingData);
      setTenantBrandingConfig(response.data);
      alert('Branding configuration updated successfully!');
      return response.data;
    } catch (error) {
      console.error('Error updating tenant branding:', error);
      // Try to create if update fails (branding doesn't exist yet)
      if (error.response?.status === 404) {
        try {
          const response = await axios.post(`${API}/tenants/${tenantId}/branding`, brandingData);
          setTenantBrandingConfig(response.data);
          alert('Branding configuration created successfully!');
          return response.data;
        } catch (createError) {
          console.error('Error creating tenant branding:', createError);
          alert(createError.response?.data?.detail || 'Error creating branding configuration');
          throw createError;
        }
      } else {
        alert(error.response?.data?.detail || 'Error updating branding configuration');
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const createSuperAdmin = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/create-super-admin`);
      alert('Super Admin created successfully! Please login with the credentials provided.');
      return response.data;
    } catch (error) {
      console.error('Error creating super admin:', error);
      alert(error.response?.data?.detail || 'Error creating Super Admin');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // NEW: Analytics & Reporting Fetch Functions
  const fetchAnalyticsDashboard = async () => {
    try {
      setIsAnalyticsLoading(true);
      const params = new URLSearchParams();
      if (analyticsDateRange.start) params.append('period_start', analyticsDateRange.start + 'T00:00:00Z');
      if (analyticsDateRange.end) params.append('period_end', analyticsDateRange.end + 'T23:59:59Z');
      
      const response = await axios.get(`${API}/analytics/dashboard?${params}`);
      setAnalyticsDashboard(response.data);
    } catch (error) {
      console.error('Error fetching analytics dashboard:', error);
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  const fetchTimeBasedMetrics = async (metricType = 'LEADS') => {
    try {
      const params = new URLSearchParams();
      params.append('period', selectedPeriod);
      if (analyticsDateRange.start) params.append('start_date', analyticsDateRange.start + 'T00:00:00Z');
      if (analyticsDateRange.end) params.append('end_date', analyticsDateRange.end + 'T23:59:59Z');
      
      const response = await axios.get(`${API}/analytics/time-based/${metricType}?${params}`);
      setTimeBasedMetrics(prev => ({ ...prev, [metricType]: response.data }));
    } catch (error) {
      console.error(`Error fetching time-based metrics for ${metricType}:`, error);
    }
  };

  const fetchAgentPerformance = async () => {
    try {
      const params = new URLSearchParams();
      if (analyticsDateRange.start) params.append('period_start', analyticsDateRange.start + 'T00:00:00Z');
      if (analyticsDateRange.end) params.append('period_end', analyticsDateRange.end + 'T23:59:59Z');
      
      const response = await axios.get(`${API}/analytics/agents?${params}`);
      setAgentPerformance(response.data);
    } catch (error) {
      console.error('Error fetching agent performance:', error);
    }
  };

  const fetchConversionFunnel = async () => {
    try {
      const params = new URLSearchParams();
      if (analyticsDateRange.start) params.append('period_start', analyticsDateRange.start + 'T00:00:00Z');
      if (analyticsDateRange.end) params.append('period_end', analyticsDateRange.end + 'T23:59:59Z');
      
      const response = await axios.get(`${API}/analytics/conversion-funnel?${params}`);
      setConversionFunnel(response.data);
    } catch (error) {
      console.error('Error fetching conversion funnel:', error);
    }
  };

  const fetchRevenueAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (analyticsDateRange.start) params.append('period_start', analyticsDateRange.start + 'T00:00:00Z');
      if (analyticsDateRange.end) params.append('period_end', analyticsDateRange.end + 'T23:59:59Z');
      
      const response = await axios.get(`${API}/analytics/revenue?${params}`);
      setRevenueAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching revenue analytics:', error);
    }
  };

  const generateCustomReport = async (reportType) => {
    try {
      setIsAnalyticsLoading(true);
      const reportRequest = {
        report_name: `${reportType} Report`,
        description: `Custom ${reportType} report generated on ${new Date().toLocaleDateString()}`,
        period_start: analyticsDateRange.start + 'T00:00:00Z',
        period_end: analyticsDateRange.end + 'T23:59:59Z',
        include_metrics: ['LEADS', 'QUOTES', 'POLICIES', 'REVENUE'],
        export_format: 'json'
      };
      
      const response = await axios.post(`${API}/analytics/custom-report`, reportRequest);
      
      // Create and download the report
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileDefaultName = `${reportType}_report_${new Date().toISOString().split('T')[0]}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      alert('Reporte generado y descargado exitosamente');
    } catch (error) {
      console.error('Error generating custom report:', error);
      alert('Error al generar reporte personalizado');
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  const exportAnalyticsData = async (dataType) => {
    try {
      let data = {};
      let filename = '';
      
      switch (dataType) {
        case 'dashboard':
          data = analyticsDashboard;
          filename = 'analytics_dashboard';
          break;
        case 'agents':
          data = agentPerformance;
          filename = 'agent_performance';
          break;
        case 'funnel':
          data = conversionFunnel;
          filename = 'conversion_funnel';
          break;
        case 'revenue':
          data = revenueAnalytics;
          filename = 'revenue_analytics';
          break;
        default:
          alert('Tipo de exportación no válido');
          return;
      }
      
      const dataStr = JSON.stringify(data, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileDefaultName = `${filename}_${new Date().toISOString().split('T')[0]}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    } catch (error) {
      console.error('Error exporting analytics data:', error);
      alert('Error al exportar datos');
    }
  };

  const refreshAnalytics = async () => {
    setIsAnalyticsLoading(true);
    try {
      await Promise.all([
        fetchAnalyticsDashboard(),
        fetchTimeBasedMetrics('LEADS'),
        fetchTimeBasedMetrics('REVENUE'),
        fetchAgentPerformance(),
        fetchConversionFunnel(),
        fetchRevenueAnalytics()
      ]);
    } catch (error) {
      console.error('Error refreshing analytics:', error);
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  const createUser = async (userData) => {
    try {
      setLoading(true);
      // Data is already in the correct format from UserForm
      await axios.post(`${API}/auth/register`, userData);
      setShowUserDialog(false);
      // Reset form handled by UserForm component
      await fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.put(`${API}/auth/profile`, profileData);
      setCurrentUser(response.data);
      localStorage.setItem('user_data', JSON.stringify(response.data));
      setShowProfileDialog(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(error.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      await axios.put(`${API}/auth/users/${userId}/status`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const createLead = async (leadData) => {
    try {
      setLoading(true);
      // Data is already transformed by LeadForm component
      await axios.post(`${API}/leads`, leadData);
      setShowCreateDialog(false);
      // Reset form handled by LeadForm component
      await fetchLeads();
    } catch (error) {
      console.error('Error creating lead:', error);
    } finally {
      setLoading(false);
    }
  };

  const createQuote = async (formData) => {
    try {
      setLoading(true);
      
      // The QuoteForm component already handles data transformation
      // so we can directly use the transformed data
      await axios.post(`${API}/quotes`, formData);
      setShowQuoteDialog(false);
      
      // Refresh quotes list
      await fetchQuotes();
    } catch (error) {
      console.error('Error creating quote:', error);
      alert('Error al crear cotización');
    } finally {
      setLoading(false);
    }
  };

  const convertQuoteToPolicy = async (quoteId) => {
    try {
      setLoading(true);
      
      // Set default dates: effective today, expires in 1 year
      const effectiveDate = new Date().toISOString();
      const expirationDate = new Date();
      expirationDate.setFullYear(expirationDate.getFullYear() + 1);
      
      const policyData = {
        quote_id: quoteId,
        effective_date: effectiveDate,
        expiration_date: expirationDate.toISOString(),
        agent_assigned: currentUser.full_name,
        notes: 'Póliza creada automáticamente desde cotización aceptada'
      };

      await axios.post(`${API}/quotes/${quoteId}/convert-to-policy`, policyData);
      
      fetchLeads();
      fetchQuotes();
      fetchPolicies();
      fetchDashboardStats();
    } catch (error) {
      console.error('Error converting quote to policy:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { status: newStatus });
      fetchLeads();
      fetchDashboardStats();
    } catch (error) {
      console.error('Error updating lead status:', error);
    }
  };

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}`, { status: newStatus });
      fetchQuotes();
      fetchDashboardStats();
    } catch (error) {
      console.error('Error updating quote status:', error);
    }
  };

  const downloadQuotePDF = async (quoteId) => {
    try {
      const response = await axios.post(`${API}/quotes/${quoteId}/generate-pdf`, {}, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cotizacion_${quoteId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const openQuoteDialog = (leadId) => {
    // Store the leadId for the QuoteForm component
    setSelectedQuoteLeadId(leadId);
    setShowQuoteDialog(true);
  };

  const openProfileDialog = () => {
    setProfileData({
      full_name: currentUser.full_name || '',
      email: currentUser.email || '',
      phone: currentUser.phone || '',
      department: currentUser.department || ''
    });
    setShowProfileDialog(true);
  };

  // NEW: Renewal Engine CRUD Functions
  const updateRenewalConfig = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.put(`${API}/renewals/config`, configUpdate);
      setShowRenewalConfigDialog(false);
      fetchRenewalConfig();
      fetchRenewalEngineStatus();
    } catch (error) {
      console.error('Error updating renewal config:', error);
      alert('Error al actualizar configuración de renovaciones');
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (taskData) => {
    try {
      setLoading(true);
      // Data is already in the correct format from TaskForm
      await axios.post(`${API}/tasks`, taskData);
      setShowTaskDialog(false);
      // Reset form handled by TaskForm component
      await fetchTasks();
    } catch (error) {
      console.error('Error creating task:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
      fetchTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const updateRenewalStatus = async (renewalId, newStatus) => {
    try {
      await axios.put(`${API}/renewals/${renewalId}`, { status: newStatus });
      fetchRenewals();
    } catch (error) {
      console.error('Error updating renewal status:', error);
    }
  };

  const triggerRenewalProcessing = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/renewals/process`);
      alert('Procesamiento de renovaciones iniciado correctamente');
      fetchRenewals();
      fetchRenewalEngineStatus();
    } catch (error) {
      console.error('Error triggering renewal processing:', error);
      alert('Error al procesar renovaciones');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Commission System CRUD Functions
  const createCommissionScheme = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/commissions/schemes`, newCommissionScheme);
      setShowCommissionSchemeDialog(false);
      setNewCommissionScheme({
        name: '',
        description: '',
        applies_to_roles: [],
        applies_to_products: [],
        period_type: 'Monthly'
      });
      fetchCommissionSchemes();
    } catch (error) {
      console.error('Error creating commission scheme:', error);
      alert('Error al crear esquema de comisiones');
    } finally {
      setLoading(false);
    }
  };

  const createCommissionRule = async (formData) => {
    try {
      setLoading(true);
      
      // The CommissionRuleForm component already handles data transformation
      // so we can directly use the transformed data
      await axios.post(`${API}/commissions/rules`, formData);
      setShowCommissionRuleDialog(false);
      
      // Refresh commission rules if we have a scheme_id
      if (formData.scheme_id) {
        fetchCommissionRules(formData.scheme_id);
      }
    } catch (error) {
      console.error('Error creating commission rule:', error);
      alert('Error al crear regla de comisión');
    } finally {
      setLoading(false);
    }
  };

  const createCommissionPeriod = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/commissions/periods`, newCommissionPeriod);
      setShowCommissionPeriodDialog(false);
      setNewCommissionPeriod({
        name: '',
        period_type: 'Monthly',
        start_date: '',
        end_date: '',
        cut_off_date: '',
        scheme_id: '',
        notes: ''
      });
      fetchCommissionPeriods();
    } catch (error) {
      console.error('Error creating commission period:', error);
      alert('Error al crear período de comisiones');
    } finally {
      setLoading(false);
    }
  };

  const calculateCommissionsForPeriod = async (periodId) => {
    try {
      setLoading(true);
      await axios.post(`${API}/commissions/periods/${periodId}/calculate`);
      alert('Cálculo de comisiones iniciado correctamente');
      fetchCommissionPeriods();
      fetchCommissions();
      fetchCommissionDashboard();
    } catch (error) {
      console.error('Error calculating commissions:', error);
      alert('Error al calcular comisiones');
    } finally {
      setLoading(false);
    }
  };

  const updateCommissionStatus = async (commissionId, newStatus) => {
    try {
      await axios.put(`${API}/commissions/${commissionId}`, { status: newStatus });
      fetchCommissions();
      fetchCommissionDashboard();
    } catch (error) {
      console.error('Error updating commission status:', error);
    }
  };

  const updateCommissionPeriodStatus = async (periodId, newStatus) => {
    try {
      await axios.put(`${API}/commissions/periods/${periodId}`, { status: newStatus });
      fetchCommissionPeriods();
    } catch (error) {
      console.error('Error updating period status:', error);
    }
  };

  const createCommissionAdjustment = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/commissions/${commissionAdjustment.commission_id}/adjustments`, {
        adjustment_amount: parseFloat(commissionAdjustment.adjustment_amount),
        reason: commissionAdjustment.reason,
        adjustment_type: commissionAdjustment.adjustment_type
      });
      setShowCommissionAdjustmentDialog(false);
      setCommissionAdjustment({
        commission_id: '',
        adjustment_amount: 0,
        reason: '',
        adjustment_type: 'Manual'
      });
      fetchCommissions();
      fetchCommissionDashboard();
      alert('Ajuste de comisión creado correctamente');
    } catch (error) {
      console.error('Error creating commission adjustment:', error);
      alert('Error al crear ajuste de comisión');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Integration System CRUD Functions
  const createIntegration = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/integrations`, newIntegration);
      setShowIntegrationDialog(false);
      setNewIntegration({
        name: '',
        description: '',
        integration_type: 'Webhook',
        api_endpoint: '',
        api_key: '',
        api_secret: '',
        webhook_url: '',
        webhook_secret: '',
        settings: {},
        headers: {},
        auto_process: true,
        duplicate_check: true,
        validate_data: true
      });
      fetchIntegrations();
      fetchIntegrationDashboard();
    } catch (error) {
      console.error('Error creating integration:', error);
      alert('Error al crear integración');
    } finally {
      setLoading(false);
    }
  };

  const updateIntegrationStatus = async (integrationId, newStatus) => {
    try {
      await axios.put(`${API}/integrations/${integrationId}`, { status: newStatus });
      fetchIntegrations();
      fetchIntegrationDashboard();
    } catch (error) {
      console.error('Error updating integration status:', error);
    }
  };

  const deleteIntegration = async (integrationId) => {
    if (confirm('¿Estás seguro de que quieres eliminar esta integración?')) {
      try {
        await axios.delete(`${API}/integrations/${integrationId}`);
        fetchIntegrations();
        fetchIntegrationDashboard();
      } catch (error) {
        console.error('Error deleting integration:', error);
        alert('Error al eliminar integración');
      }
    }
  };

  const createFieldMapping = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/integrations/${newFieldMapping.integration_id}/field-mappings`, newFieldMapping);
      setShowFieldMappingDialog(false);
      setNewFieldMapping({
        integration_id: '',
        source_field: '',
        target_field: '',
        mapping_type: 'Direct',
        transformation_rule: '',
        condition: '',
        default_value: '',
        required: false,
        validation_rule: ''
      });
      if (selectedIntegration) {
        fetchFieldMappings(selectedIntegration.id);
      }
    } catch (error) {
      console.error('Error creating field mapping:', error);
      alert('Error al crear mapeo de campo');
    } finally {
      setLoading(false);
    }
  };

  const deleteFieldMapping = async (mappingId) => {
    if (confirm('¿Estás seguro de que quieres eliminar este mapeo?')) {
      try {
        await axios.delete(`${API}/field-mappings/${mappingId}`);
        if (selectedIntegration) {
          fetchFieldMappings(selectedIntegration.id);
        }
      } catch (error) {
        console.error('Error deleting field mapping:', error);
        alert('Error al eliminar mapeo de campo');
      }
    }
  };

  const testIntegration = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post(`${API}/integrations/${testIntegrationData.integration_id}/test`, testIntegrationData.test_data);
      setShowTestIntegrationDialog(false);
      setTestIntegrationData({ integration_id: '', test_data: {} });
      
      if (response.data.success) {
        alert('Test exitoso! Lead procesado correctamente.');
      } else {
        alert('Test completado con errores. Revisar configuración.');
      }
      
      fetchIncomingLeads();
    } catch (error) {
      console.error('Error testing integration:', error);
      alert('Error al probar integración');
    } finally {
      setLoading(false);
    }
  };

  const reprocessIncomingLead = async (leadId) => {
    try {
      setLoading(true);
      await axios.post(`${API}/incoming-leads/${leadId}/reprocess`);
      fetchIncomingLeads();
      alert('Lead reprocesado correctamente');
    } catch (error) {
      console.error('Error reprocessing lead:', error);
      alert('Error al reprocesar lead');
    } finally {
      setLoading(false);
    }
  };

  const getProductIcon = (productType) => {
    switch (productType) {
      case 'Auto': return <Car className="h-4 w-4" />;
      case 'Casa': return <Home className="h-4 w-4" />;
      case 'Comercial': return <Building className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getExpirationAlert = (daysUntilExpiration) => {
    if (daysUntilExpiration <= 7) {
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    } else if (daysUntilExpiration <= 30) {
      return <Clock className="h-4 w-4 text-yellow-500" />;
    }
    return <CheckCircle className="h-4 w-4 text-green-500" />;
  };

  // Dialog Components - Define before main return to avoid initialization errors
  const RenewalConfigDialog = () => (
    <Dialog open={showRenewalConfigDialog} onOpenChange={setShowRenewalConfigDialog}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-mg-green">Configuración del Motor de Renovaciones</DialogTitle>
          <DialogDescription>
            Configure el comportamiento automático del sistema de renovaciones.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={updateRenewalConfig} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="days_before">Días antes del vencimiento</Label>
              <Input
                id="days_before"
                type="number"
                min="1"
                max="365"
                value={configUpdate.days_before_expiration}
                onChange={(e) => setConfigUpdate({
                  ...configUpdate,
                  days_before_expiration: parseInt(e.target.value)
                })}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Cuántos días antes del vencimiento se iniciará el proceso de renovación
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-mg-green">Acciones Automáticas</h3>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="auto_quotes"
                checked={configUpdate.auto_generate_quotes}
                onCheckedChange={(checked) => setConfigUpdate({
                  ...configUpdate,
                  auto_generate_quotes: checked
                })}
              />
              <Label htmlFor="auto_quotes">Generar cotizaciones automáticamente</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="auto_notifications"
                checked={configUpdate.auto_send_notifications}
                onCheckedChange={(checked) => setConfigUpdate({
                  ...configUpdate,
                  auto_send_notifications: checked
                })}
              />
              <Label htmlFor="auto_notifications">Enviar notificaciones automáticamente</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="auto_tasks"
                checked={configUpdate.auto_create_tasks}
                onCheckedChange={(checked) => setConfigUpdate({
                  ...configUpdate,
                  auto_create_tasks: checked
                })}
              />
              <Label htmlFor="auto_tasks">Crear tareas automáticamente</Label>
            </div>
          </div>

          {configUpdate.auto_send_notifications && (
            <div>
              <Label>Tipos de notificación a enviar</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {NOTIFICATION_TYPES.map(type => (
                  <div key={type} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`notification_${type}`}
                      checked={configUpdate.notification_types.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setConfigUpdate({
                            ...configUpdate,
                            notification_types: [...configUpdate.notification_types, type]
                          });
                        } else {
                          setConfigUpdate({
                            ...configUpdate,
                            notification_types: configUpdate.notification_types.filter(t => t !== type)
                          });
                        }
                      }}
                    />
                    <Label htmlFor={`notification_${type}`}>{type}</Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowRenewalConfigDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-mg-green hover:bg-mg-green/90 text-white"
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar Configuración'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  const TaskDialog = () => (
    <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-mg-green">Nueva Tarea</DialogTitle>
          <DialogDescription>
            Cree una nueva tarea para el sistema.
          </DialogDescription>
        </DialogHeader>
        
        <TaskForm 
          onSubmit={createTask}
          onCancel={() => setShowTaskDialog(false)}
          loading={loading}
          users={users}
        />
      </DialogContent>
    </Dialog>
  );

  // Commission Rule Dialog - NOW USING REACT HOOK FORM
  const CommissionRuleDialog = () => (
    <Dialog open={showCommissionRuleDialog} onOpenChange={setShowCommissionRuleDialog}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-mg-green">{t('commissions.createRule')}</DialogTitle>
          <DialogDescription>
            {t('commissions.createRuleDescription')}
          </DialogDescription>
        </DialogHeader>
        
        <CommissionRuleForm
          onSubmit={createCommissionRule}
          onCancel={() => setShowCommissionRuleDialog(false)}
          loading={loading}
        />
      </DialogContent>
    </Dialog>
  );

  const CommissionPeriodDialog = () => (
    <Dialog open={showCommissionPeriodDialog} onOpenChange={setShowCommissionPeriodDialog}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-mg-green">Crear Período de Comisión</DialogTitle>
          <DialogDescription>
            Configure un nuevo período para el cálculo de comisiones.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={createCommissionPeriod} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="period_name">Nombre del Período*</Label>
              <Input
                id="period_name"
                placeholder="ej: Enero 2024, Q1 2024"
                value={newCommissionPeriod.name}
                onChange={(e) => handleCommissionPeriodFieldChange('name', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="period_type_select">Tipo de Período</Label>
              <Select
                value={newCommissionPeriod.period_type}
                onValueChange={(value) => setNewCommissionPeriod({ ...newCommissionPeriod, period_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMISSION_PERIOD_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="scheme_select">Esquema de Comisión*</Label>
              <Select
                value={newCommissionPeriod.scheme_id}
                onValueChange={(value) => setNewCommissionPeriod({ ...newCommissionPeriod, scheme_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar esquema" />
                </SelectTrigger>
                <SelectContent>
                  {commissionSchemes.filter(scheme => scheme.is_active).map(scheme => (
                    <SelectItem key={scheme.id} value={scheme.id}>{scheme.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="start_date">Fecha de Inicio*</Label>
              <Input
                id="start_date"
                type="date"
                value={newCommissionPeriod.start_date}
                onChange={(e) => setNewCommissionPeriod({ ...newCommissionPeriod, start_date: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="end_date">Fecha de Fin*</Label>
              <Input
                id="end_date"
                type="date"
                value={newCommissionPeriod.end_date}
                onChange={(e) => setNewCommissionPeriod({ ...newCommissionPeriod, end_date: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="cut_off_date">Fecha de Corte*</Label>
              <Input
                id="cut_off_date"
                type="date"
                value={newCommissionPeriod.cut_off_date}
                onChange={(e) => setNewCommissionPeriod({ ...newCommissionPeriod, cut_off_date: e.target.value })}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Fecha límite para incluir pólizas en el período
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="period_notes">Notas</Label>
            <Textarea
              id="period_notes"
              value={newCommissionPeriod.notes}
              onChange={(e) => setNewCommissionPeriod({ ...newCommissionPeriod, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCommissionPeriodDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-mg-green hover:bg-mg-green/90 text-white"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Período'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  const CommissionAdjustmentDialog = () => (
    <Dialog open={showCommissionAdjustmentDialog} onOpenChange={setShowCommissionAdjustmentDialog}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-mg-green">Ajuste de Comisión</DialogTitle>
          <DialogDescription>
            Crear un ajuste manual para una comisión específica.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={createCommissionAdjustment} className="space-y-6">
          <div>
            <Label htmlFor="commission_select">Comisión*</Label>
            <Select
              value={commissionAdjustment.commission_id}
              onValueChange={(value) => setCommissionAdjustment({ ...commissionAdjustment, commission_id: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar comisión" />
              </SelectTrigger>
              <SelectContent>
                {commissions.map(commission => (
                  <SelectItem key={commission.id} value={commission.id}>
                    {commission.client_name} - ${commission.commission_amount?.toLocaleString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="adjustment_amount">Monto de Ajuste ($)*</Label>
            <Input
              id="adjustment_amount"
              type="number"
              step="0.01"
              placeholder="Positivo para aumentar, negativo para reducir"
              value={commissionAdjustment.adjustment_amount}
              onChange={(e) => setCommissionAdjustment({ ...commissionAdjustment, adjustment_amount: parseFloat(e.target.value) || 0 })}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Usar valores negativos para reducir la comisión
            </p>
          </div>

          <div>
            <Label htmlFor="adjustment_type">Tipo de Ajuste</Label>
            <Select
              value={commissionAdjustment.adjustment_type}
              onValueChange={(value) => setCommissionAdjustment({ ...commissionAdjustment, adjustment_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Manual">Manual</SelectItem>
                <SelectItem value="Policy Cancellation">Cancelación de Póliza</SelectItem>
                <SelectItem value="Correction">Corrección</SelectItem>
                <SelectItem value="Bonus">Bonificación</SelectItem>
                <SelectItem value="Penalty">Penalización</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="adjustment_reason">Razón del Ajuste*</Label>
            <Textarea
              id="adjustment_reason"
              value={commissionAdjustment.reason}
              onChange={(e) => setCommissionAdjustment({ ...commissionAdjustment, reason: e.target.value })}
              required
              rows={3}
              placeholder="Describe la razón del ajuste..."
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCommissionAdjustmentDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-mg-green hover:bg-mg-green/90 text-white"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Ajuste'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  // NEW: Integration System Dialogs - Define before main return to avoid initialization errors
  const IntegrationDialog = () => (
    <Dialog open={showIntegrationDialog} onOpenChange={setShowIntegrationDialog}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-mg-green">Crear Nueva Integración</DialogTitle>
          <DialogDescription>
            Configure una nueva integración para capturar leads automáticamente.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={createIntegration} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="integration_name">Nombre de la Integración*</Label>
              <Input
                id="integration_name"
                value={newIntegration.name}
                onChange={(e) => handleIntegrationFieldChange('name', e.target.value)}
                required
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="integration_description">Descripción</Label>
              <Textarea
                id="integration_description"
                value={newIntegration.description}
                onChange={(e) => handleIntegrationFieldChange('description', e.target.value)}
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="integration_type">Tipo de Integración*</Label>
              <Select
                value={newIntegration.integration_type}
                onValueChange={(value) => setNewIntegration({ ...newIntegration, integration_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {INTEGRATION_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Configuration based on integration type */}
          {(newIntegration.integration_type === 'API REST' || newIntegration.integration_type === 'Custom') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="api_endpoint">API Endpoint*</Label>
                <Input
                  id="api_endpoint"
                  type="url"
                  value={newIntegration.api_endpoint}
                  onChange={(e) => handleIntegrationFieldChange('api_endpoint', e.target.value)}
                  placeholder="https://api.example.com/leads"
                />
              </div>
              <div>
                <Label htmlFor="api_key">API Key</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={newIntegration.api_key}
                  onChange={(e) => handleIntegrationFieldChange('api_key', e.target.value)}
                  placeholder="API Key"
                />
              </div>
            </div>
          )}

          {newIntegration.integration_type === 'Webhook' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="webhook_url">Webhook URL*</Label>
                <Input
                  id="webhook_url"
                  type="url"
                  value={newIntegration.webhook_url}
                  onChange={(e) => setNewIntegration({ ...newIntegration, webhook_url: e.target.value })}
                  placeholder="https://your-domain.com/webhook"
                />
              </div>
              <div>
                <Label htmlFor="webhook_secret">Webhook Secret</Label>
                <Input
                  id="webhook_secret"
                  type="password"
                  value={newIntegration.webhook_secret}
                  onChange={(e) => setNewIntegration({ ...newIntegration, webhook_secret: e.target.value })}
                  placeholder="Secret para validar webhook"
                />
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-mg-green">Opciones de Procesamiento</h3>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="auto_process"
                checked={newIntegration.auto_process}
                onCheckedChange={(checked) => setNewIntegration({
                  ...newIntegration,
                  auto_process: checked
                })}
              />
              <Label htmlFor="auto_process">Procesar leads automáticamente</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="duplicate_check"
                checked={newIntegration.duplicate_check}
                onCheckedChange={(checked) => setNewIntegration({
                  ...newIntegration,
                  duplicate_check: checked
                })}
              />
              <Label htmlFor="duplicate_check">Verificar duplicados</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="validate_data"
                checked={newIntegration.validate_data}
                onCheckedChange={(checked) => setNewIntegration({
                  ...newIntegration,
                  validate_data: checked
                })}
              />
              <Label htmlFor="validate_data">Validar datos entrantes</Label>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowIntegrationDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-mg-green hover:bg-mg-green/90 text-white"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Integración'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  const FieldMappingDialog = () => (
    <Dialog open={showFieldMappingDialog} onOpenChange={setShowFieldMappingDialog}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-mg-green">Crear Field Mapping</DialogTitle>
          <DialogDescription>
            Configure cómo mapear campos de la fuente externa a campos del CRM.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={createFieldMapping} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="source_field">Campo Fuente*</Label>
              <Input
                id="source_field"
                value={newFieldMapping.source_field}
                onChange={(e) => handleFieldMappingFieldChange('source_field', e.target.value)}
                placeholder="nombre_completo, email, telefono"
                required
              />
            </div>

            <div>
              <Label htmlFor="target_field">Campo Destino*</Label>
              <Select
                value={newFieldMapping.target_field}
                onValueChange={(value) => setNewFieldMapping({ ...newFieldMapping, target_field: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar campo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Nombre</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Teléfono</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="address">Dirección</SelectItem>
                  <SelectItem value="product_type">Tipo de Producto</SelectItem>
                  <SelectItem value="source">Fuente</SelectItem>
                  <SelectItem value="campaign">Campaña</SelectItem>
                  <SelectItem value="zip_code">Código Postal</SelectItem>
                  <SelectItem value="notes">Notas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="mapping_type">Tipo de Mapeo*</Label>
              <Select
                value={newFieldMapping.mapping_type}
                onValueChange={(value) => setNewFieldMapping({ ...newFieldMapping, mapping_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_MAPPING_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="required_field"
                checked={newFieldMapping.required}
                onCheckedChange={(checked) => setNewFieldMapping({
                  ...newFieldMapping,
                  required: checked
                })}
              />
              <Label htmlFor="required_field">Campo requerido</Label>
            </div>
          </div>

          {/* Conditional fields based on mapping type */}
          {newFieldMapping.mapping_type === 'Computed' && (
            <div>
              <Label htmlFor="transformation_rule">Regla de Transformación*</Label>
              <Textarea
                id="transformation_rule"
                value={newFieldMapping.transformation_rule}
                onChange={(e) => setNewFieldMapping({ ...newFieldMapping, transformation_rule: e.target.value })}
                placeholder="ej: value.toUpperCase(), value.replace(/[^0-9]/g, '')"
                rows={2}
              />
            </div>
          )}

          {newFieldMapping.mapping_type === 'Conditional' && (
            <div>
              <Label htmlFor="condition">Condición*</Label>
              <Textarea
                id="condition"
                value={newFieldMapping.condition}
                onChange={(e) => setNewFieldMapping({ ...newFieldMapping, condition: e.target.value })}
                placeholder="ej: value === 'auto' ? 'Auto' : 'Casa'"
                rows={2}
              />
            </div>
          )}

          {newFieldMapping.mapping_type === 'Default' && (
            <div>
              <Label htmlFor="default_value">Valor por Defecto*</Label>
              <Input
                id="default_value"
                value={newFieldMapping.default_value}
                onChange={(e) => setNewFieldMapping({ ...newFieldMapping, default_value: e.target.value })}
                placeholder="Valor fijo a asignar"
              />
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFieldMappingDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-mg-green hover:bg-mg-green/90 text-white"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Mapping'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  const TestIntegrationDialog = () => (
    <Dialog open={showTestIntegrationDialog} onOpenChange={setShowTestIntegrationDialog}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-mg-green">Probar Integración</DialogTitle>
          <DialogDescription>
            Envíe datos de prueba para verificar que la integración funciona correctamente.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={testIntegration} className="space-y-6">
          <div>
            <Label htmlFor="test_data">Datos de Prueba (JSON)*</Label>
            <Textarea
              id="test_data"
              value={JSON.stringify(testIntegrationData.test_data, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setTestIntegrationData({ ...testIntegrationData, test_data: parsed });
                } catch (error) {
                  // Invalid JSON, keep the string value for user to fix
                }
              }}
              placeholder='{"name": "Juan Pérez", "email": "juan@example.com", "phone": "555-1234"}'
              rows={8}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Ingrese datos JSON válidos que simulen lo que recibiría la integración
            </p>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowTestIntegrationDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-mg-green hover:bg-mg-green/90 text-white"
              disabled={loading}
            >
              {loading ? 'Probando...' : 'Probar Integración'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  // NEW: Integration System View Component - Define before main return to avoid initialization errors
  const IntegrationsViewWrapper = () => (
    <IntegrationsView
      integrations={integrations}
      integrationDashboard={integrationDashboard}
      selectedIntegration={selectedIntegration}
      fieldMappings={fieldMappings}
      incomingLeads={incomingLeads}
      hasPermission={hasPermission}
      onCreateIntegration={createIntegration}
      onUpdateIntegrationStatus={updateIntegrationStatus}
      onDeleteIntegration={deleteIntegration}
      onSelectIntegration={(integration) => {
        setSelectedIntegration(integration);
        fetchFieldMappings(integration.id);
      }}
      onCreateFieldMapping={createFieldMapping}
      onDeleteFieldMapping={deleteFieldMapping}
      onTestIntegration={testIntegration}
      onReprocessLead={reprocessIncomingLead}
      setShowIntegrationDialog={setShowIntegrationDialog}
      setShowFieldMappingDialog={() => {
        if (selectedIntegration) {
          setNewFieldMapping({ ...newFieldMapping, integration_id: selectedIntegration.id });
        }
        setShowFieldMappingDialog(true);
      }}
      setShowTestIntegrationDialog={() => {
        if (selectedIntegration) {
          setTestIntegrationData({ ...testIntegrationData, integration_id: selectedIntegration.id });
        }
        setShowTestIntegrationDialog(true);
      }}
    />
  );

  // NEW: Communication Hub View Component - Define before main return to avoid initialization errors
  const CommunicationsView = () => (
    <div className="space-y-6">
      <Tabs value={activeCommunicationTab} onValueChange={setActiveCommunicationTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard" className="flex items-center">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center">
            <Template className="h-4 w-4 mr-2" />
            Plantillas
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center">
            <Clock className="h-4 w-4 mr-2" />
            Historial
          </TabsTrigger>
          {hasPermission(['Admin']) && (
            <TabsTrigger value="settings" className="flex items-center">
              <Settings className="h-4 w-4 mr-2" />
              Configuración
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="dashboard">
          <CommunicationDashboard 
            stats={communicationStats}
            onRefresh={fetchCommunicationStats}
          />
        </TabsContent>

        <TabsContent value="templates">
          <CommunicationTemplates
            templates={communicationTemplates}
            currentUser={currentUser}
            onCreateTemplate={() => {
              setSelectedTemplate(null);
              setShowTemplateDialog(true);
            }}
            onEditTemplate={(template) => {
              setSelectedTemplate(template);
              setShowTemplateDialog(true);
            }}
            onDeleteTemplate={deleteCommunicationTemplate}
            onTestTemplate={(template) => {
              setSelectedTemplate(template);
              setShowTestCommunicationDialog(true);
            }}
          />
        </TabsContent>

        <TabsContent value="logs">
          <CommunicationLogs
            logs={communicationLogs}
            onRefresh={fetchCommunicationLogs}
          />
        </TabsContent>

        {hasPermission(['Admin']) && (
          <TabsContent value="settings">
            <CommunicationSettings
              settings={communicationSettings}
              currentUser={currentUser}
              onUpdateSettings={updateCommunicationSettings}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-mg-green text-white rounded-lg flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-bold">CIP</span>
          </div>
          <h2 className="text-2xl font-semibold text-mg-green mb-2">CRM INS PRO</h2>
          <p className="text-gray-600">Cargando sistema...</p>
          <div className="mt-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mg-green mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  const Dashboard = () => (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card className="border-l-4 border-l-mg-green">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.stats.totalLeads')}</CardTitle>
            <Users className="h-5 w-5 text-mg-green" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-mg-green">{dashboardStats.total_leads || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-mg-yellow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.stats.totalQuotes')}</CardTitle>
            <FileText className="h-5 w-5 text-mg-yellow" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-mg-yellow">{dashboardStats.total_quotes || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-mg-green">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.stats.totalPolicies')}</CardTitle>
            <Shield className="h-5 w-5 text-mg-green" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-mg-green">{dashboardStats.total_policies || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-mg-yellow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.stats.totalRevenue')}</CardTitle>
            <DollarSign className="h-5 w-5 text-mg-yellow" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-mg-yellow">${(dashboardStats.policies_value || 0).toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">{t('policies.statusOptions.expired')}</CardTitle>
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-500">{dashboardStats.expiring_policies || 0}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-mg-green">{t('dashboard.charts.leadsByStatus')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(dashboardStats.leads_by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <Badge className={STATUS_COLORS[status]}>{translateLeadStatus(status)}</Badge>
                  <span className="font-semibold text-mg-green">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-mg-green">{t('dashboard.charts.quotesByCarrier')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(dashboardStats.quotes_by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <Badge className={QUOTE_STATUS_COLORS[status]}>{translateQuoteStatus(status)}</Badge>
                  <span className="font-semibold text-mg-green">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-mg-green">{hasPermission(['Admin', 'Manager']) ? t('users.title') : t('common.all')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(dashboardStats.policies_by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <Badge className={POLICY_STATUS_COLORS[status]}>{translatePolicyStatus(status)}</Badge>
                  <span className="font-semibold text-mg-green">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-mg-green">
              {hasPermission(['Admin', 'Manager']) ? t('users.title') : t('common.all')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {hasPermission(['Admin', 'Manager']) ? (
                Object.entries(dashboardStats.users_by_role || {}).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between">
                    <Badge className={ROLE_COLORS[role]}>{translateUserRole(role)}</Badge>
                    <span className="font-semibold text-mg-green">{count}</span>
                  </div>
                ))
              ) : (
                Object.entries(dashboardStats.leads_by_product || {}).map(([product, count]) => (
                  <div key={product} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getProductIcon(product)}
                      <span className="text-gray-700">{product}</span>
                    </div>
                    <span className="font-semibold text-mg-green">{count}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Policies Alert */}
      {dashboardStats.expiring_policies > 0 && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <AlertDescription className="text-red-700">
            Tienes {dashboardStats.expiring_policies} póliza{dashboardStats.expiring_policies > 1 ? 's' : ''} que vence{dashboardStats.expiring_policies > 1 ? 'n' : ''} en los próximos 30 días. 
            <Button 
              variant="link" 
              className="p-0 h-auto text-red-700 underline font-semibold ml-1"
              onClick={() => setActiveView('policies')}
            >
              Ver pólizas
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  const LeadsPipeline = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-mg-green">Pipeline de Leads</h2>
        {hasPermission(['Admin', 'Manager', 'Agente']) && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-mg-green hover:bg-mg-green/90 text-white">
                <Plus className="h-4 w-4 mr-2" />
                {t('leads.newLead')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-mg-green">{t('leads.createLead')}</DialogTitle>
                <DialogDescription>{t('leads.leadDetails')}</DialogDescription>
              </DialogHeader>
              
              <LeadForm 
                onSubmit={createLead}
                onCancel={() => setShowCreateDialog(false)}
                loading={loading}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {LEAD_STATUSES.map((status, index) => (
          <div key={status} className="space-y-4">
            <div className="text-center">
              <Badge className={`${STATUS_COLORS[status]} text-lg px-4 py-2`}>
                {getTranslatedLeadStatuses()[index]}
              </Badge>
              <div className="text-sm text-gray-500 mt-1">
                {leads.filter(lead => lead.status === status).length} leads
              </div>
            </div>
            
            <div className="space-y-3 min-h-[300px]">
              {leads.filter(lead => lead.status === status).map(lead => (
                <Card key={lead.id} className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-mg-green">
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-mg-green">{lead.contact.name}</h3>
                        {getProductIcon(lead.product_type)}
                      </div>
                      <p className="text-sm text-gray-600">{lead.product_type}</p>
                      <div className="flex items-center text-xs text-gray-500 space-x-2">
                        {lead.contact.phone && (
                          <div className="flex items-center">
                            <Phone className="h-3 w-3 mr-1" />
                            {lead.contact.phone}
                          </div>
                        )}
                        {lead.contact.email && (
                          <div className="flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {lead.contact.email}
                          </div>
                        )}
                      </div>
                      {lead.source && (
                        <Badge variant="outline" className="text-xs">
                          {lead.source}
                        </Badge>
                      )}
                      
                      <div className="space-y-2">
                        {hasPermission(['Admin', 'Manager', 'Agente']) && (
                          <Select
                            value={lead.status}
                            onValueChange={(newStatus) => updateLeadStatus(lead.id, newStatus)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getTranslatedLeadStatuses().map((statusOption, index) => (
                                <SelectItem key={LEAD_STATUSES[index]} value={LEAD_STATUSES[index]}>
                                  {statusOption}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        
                        {hasPermission(['Admin', 'Manager', 'Agente']) && lead.status !== 'Cerrado' && lead.status !== 'Perdido' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full text-xs border-mg-green text-mg-green hover:bg-mg-green hover:text-white"
                            onClick={() => openQuoteDialog(lead.id)}
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            Cotizar
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const QuotesView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-mg-green">Cotizaciones</h2>
      </div>

      <div className="grid gap-6">
        {quotes.map(quote => (
          <Card key={quote.id} className="border-l-4 border-l-mg-green">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getProductIcon(quote.product_type)}
                  <div>
                    <CardTitle className="text-mg-green">
                      {quote.lead.contact.name} - {quote.product_type}
                    </CardTitle>
                    <CardDescription>
                      {quote.carrier} • Cotización #{quote.id.substring(0, 8)}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className={QUOTE_STATUS_COLORS[quote.status]}>
                    {quote.status}
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadQuotePDF(quote.id)}
                  >
                    <Download className="h-3 w-3 mr-1" />
                    PDF
                  </Button>
                  {hasPermission(['Admin', 'Manager', 'Agente']) && (quote.status === 'Enviada' || quote.status === 'Borrador') && (
                    <Button
                      size="sm"
                      className="bg-mg-green hover:bg-mg-green/90 text-white"
                      onClick={() => convertQuoteToPolicy(quote.id)}
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      Crear Póliza
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Prima Anual</Label>
                  <p className="text-2xl font-bold text-mg-yellow">
                    ${quote.annual_premium.toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Deducible</Label>
                  <p className="text-lg font-semibold">
                    ${quote.deductible ? quote.deductible.toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Válida Hasta</Label>
                  <p className="text-lg font-semibold">
                    {new Date(quote.valid_until).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Estado</Label>
                  {hasPermission(['Admin', 'Manager', 'Agente']) ? (
                    <Select
                      value={quote.status}
                      onValueChange={(newStatus) => updateQuoteStatus(quote.id, newStatus)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {QUOTE_STATUSES.map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={QUOTE_STATUS_COLORS[quote.status]}>
                      {quote.status}
                    </Badge>
                  )}
                </div>
              </div>
              
              {quote.notes && (
                <div className="mt-4 pt-4 border-t">
                  <Label className="text-sm font-medium text-gray-600">Notas</Label>
                  <p className="text-sm text-gray-700">{quote.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const PoliciesView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-mg-green">Pólizas</h2>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => fetchPolicies()}
            className="border-mg-green text-mg-green hover:bg-mg-green hover:text-white"
          >
            Actualizar
          </Button>
        </div>
      </div>

      <div className="grid gap-6">
        {policies.map(policy => (
          <Card key={policy.id} className="border-l-4 border-l-mg-green">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Shield className="h-6 w-6 text-mg-green" />
                  <div>
                    <CardTitle className="text-mg-green">
                      {policy.quote.lead.contact.name} - {policy.product_type}
                    </CardTitle>
                    <CardDescription>
                      {policy.carrier} • Póliza #{policy.policy_number}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className={POLICY_STATUS_COLORS[policy.status]}>
                    {policy.status}
                  </Badge>
                  {getExpirationAlert(policy.days_until_expiration)}
                  <span className="text-sm text-gray-600">
                    {policy.days_until_expiration > 0 
                      ? `${policy.days_until_expiration} días` 
                      : `Vencida hace ${Math.abs(policy.days_until_expiration)} días`
                    }
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Prima Anual</Label>
                  <p className="text-2xl font-bold text-mg-yellow">
                    ${policy.annual_premium.toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Deducible</Label>
                  <p className="text-lg font-semibold">
                    ${policy.deductible ? policy.deductible.toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Vigente Desde</Label>
                  <p className="text-sm">
                    {new Date(policy.effective_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Vence</Label>
                  <p className="text-sm">
                    {new Date(policy.expiration_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Agente</Label>
                  <p className="text-sm">{policy.agent_assigned || 'No asignado'}</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Contacto</Label>
                    <div className="space-y-1">
                      {policy.quote.lead.contact.phone && (
                        <p className="text-sm flex items-center">
                          <Phone className="h-3 w-3 mr-2" />
                          {policy.quote.lead.contact.phone}
                        </p>
                      )}
                      {policy.quote.lead.contact.email && (
                        <p className="text-sm flex items-center">
                          <Mail className="h-3 w-3 mr-2" />
                          {policy.quote.lead.contact.email}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {policy.notes && (
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Notas</Label>
                      <p className="text-sm text-gray-700">{policy.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Expiration Warning */}
              {policy.status === 'Activa' && policy.days_until_expiration <= 30 && (
                <div className="mt-4 pt-4 border-t">
                  <Alert className={`${policy.days_until_expiration <= 7 ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'}`}>
                    {policy.days_until_expiration <= 7 ? (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    ) : (
                      <Clock className="h-4 w-4 text-yellow-500" />
                    )}
                    <AlertDescription className={policy.days_until_expiration <= 7 ? 'text-red-700' : 'text-yellow-700'}>
                      Esta póliza vence en {policy.days_until_expiration} día{policy.days_until_expiration !== 1 ? 's' : ''}. 
                      Es necesario contactar al cliente para renovación.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const UsersView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-mg-green">{t('users.title')}</h2>
        <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
          <DialogTrigger asChild>
            <Button className="bg-mg-green hover:bg-mg-green/90 text-white">
              <UserPlus className="h-4 w-4 mr-2" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-mg-green">Crear Nuevo Usuario</DialogTitle>
              <DialogDescription>Complete la información del usuario.</DialogDescription>
            </DialogHeader>
            
            <UserForm 
              onSubmit={createUser}
              onCancel={() => setShowUserDialog(false)}
              loading={loading}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6">
        {users.map(user => (
          <Card key={user.id} className="border-l-4 border-l-mg-green">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <User className="h-6 w-6 text-mg-green" />
                  <div>
                    <CardTitle className="text-mg-green">
                      {user.full_name}
                    </CardTitle>
                    <CardDescription>
                      @{user.username} • {user.email}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className={ROLE_COLORS[user.role]}>
                    {user.role}
                  </Badge>
                  <Badge className={user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {user.is_active ? 'Activo' : 'Inactivo'}
                  </Badge>
                  <Button
                    size="sm"
                    variant={user.is_active ? "destructive" : "default"}
                    onClick={() => toggleUserStatus(user.id)}
                    className={user.is_active ? '' : 'bg-mg-green hover:bg-mg-green/90'}
                  >
                    {user.is_active ? 'Desactivar' : 'Activar'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Teléfono</Label>
                  <p className="text-sm">{user.phone || 'No especificado'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Departamento</Label>
                  <p className="text-sm">{user.department || 'No especificado'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Creado</Label>
                  <p className="text-sm">
                    {new Date(user.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Quote Creation Dialog - NOW USING REACT HOOK FORM
  const QuoteDialog = () => {
    if (!hasPermission(['Admin', 'Manager', 'Agente'])) {
      return null;
    }
    
    return (
      <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-mg-green">{t('quotes.createQuote')}</DialogTitle>
            <DialogDescription>
              {t('quotes.createQuoteDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <QuoteForm
            onSubmit={createQuote}
            onCancel={() => setShowQuoteDialog(false)}
            loading={loading}
            leadId={selectedQuoteLeadId}
          />
        </DialogContent>
      </Dialog>
    );
  };

  // Profile Dialog
  const ProfileDialog = () => (
    <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-mg-green">Mi Perfil</DialogTitle>
          <DialogDescription>Actualizar información personal</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={updateProfile} className="space-y-4">
          <div>
            <Label htmlFor="profile_full_name">Nombre Completo</Label>
            <Input
              id="profile_full_name"
              value={profileData.full_name}
              onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="profile_email">Email</Label>
            <Input
              id="profile_email"
              type="email"
              value={profileData.email}
              onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="profile_phone">Teléfono</Label>
            <Input
              id="profile_phone"
              value={profileData.phone}
              onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="profile_department">Departamento</Label>
            <Input
              id="profile_department"
              value={profileData.department}
              onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setShowProfileDialog(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-mg-green hover:bg-mg-green/90" disabled={loading}>
              {loading ? 'Actualizando...' : 'Actualizar Perfil'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  // NEW: Renewal Engine Components - Moved before main return to fix initialization order
  const RenewalsView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-mg-green">Motor de Renovaciones</h1>
          <p className="text-gray-600 mt-2">Gestión automática de renovaciones de pólizas</p>
        </div>
        <div className="flex gap-2">
          {hasPermission(['Admin']) && (
            <>
              <Button
                onClick={() => setShowRenewalConfigDialog(true)}
                variant="outline"
                className="border-mg-green text-mg-green hover:bg-mg-green hover:text-white"
              >
                <Settings className="h-4 w-4 mr-2" />
                Configuración
              </Button>
              <Button
                onClick={triggerRenewalProcessing}
                className="bg-mg-green hover:bg-mg-green/90 text-white"
                disabled={loading}
              >
                <Zap className="h-4 w-4 mr-2" />
                Procesar Renovaciones
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Engine Status Dashboard */}
      {renewalEngineStatus && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-mg-green">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Renovaciones</CardTitle>
              <RotateCcw className="h-5 w-5 text-mg-green" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-mg-green">{renewalEngineStatus.total_renewals || 0}</div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-mg-yellow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Pendientes</CardTitle>
              <Clock className="h-5 w-5 text-mg-yellow" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-mg-yellow">{renewalEngineStatus.pending_renewals || 0}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Procesando</CardTitle>
              <TrendingUp className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-500">{renewalEngineStatus.processing_renewals || 0}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Completadas</CardTitle>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-500">{renewalEngineStatus.completed_renewals || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Renewals List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-mg-green">Renovaciones Activas</CardTitle>
          <CardDescription>
            Pólizas programadas para renovación automática
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {renewals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <RotateCcw className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No hay renovaciones programadas</p>
              </div>
            ) : (
              renewals.map(renewal => (
                <div key={renewal.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      {getProductIcon(renewal.policy.product_type)}
                      <div>
                        <h3 className="font-semibold text-mg-green">
                          {renewal.policy.quote.lead.contact.name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Póliza #{renewal.policy.policy_number} • {renewal.policy.product_type}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Vence</p>
                      <p className="font-semibold">
                        {new Date(renewal.policy.expiration_date).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className={RENEWAL_STATUS_COLORS[renewal.status]}>
                      {renewal.status}
                    </Badge>
                    {hasPermission(['Admin', 'Manager']) && (
                      <Select
                        value={renewal.status}
                        onValueChange={(newStatus) => updateRenewalStatus(renewal.id, newStatus)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RENEWAL_STATUSES.map(status => (
                            <SelectItem key={status} value={status}>{status}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const TasksView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-mg-green">{t('tasks.title')}</h1>
          <p className="text-gray-600 mt-2">{t('tasks.taskDetails')}</p>
        </div>
        {hasPermission(['Admin', 'Manager', 'Agente']) && (
          <Button
            onClick={() => setShowTaskDialog(true)}
            className="bg-mg-green hover:bg-mg-green/90 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva Tarea
          </Button>
        )}
      </div>

      {/* Tasks List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-mg-green">Tareas Activas</CardTitle>
          <CardDescription>
            Tareas pendientes y en progreso del sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ListTodo className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No hay tareas registradas</p>
              </div>
            ) : (
              tasks.map(task => (
                <div key={task.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="font-semibold text-mg-green">{task.title}</h3>
                      <p className="text-sm text-gray-600">{task.description}</p>
                      {task.assigned_user_name && (
                        <p className="text-xs text-gray-500 mt-1">
                          Asignado a: {task.assigned_user_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Badge className={TASK_PRIORITY_COLORS[task.priority]}>
                      {task.priority}
                    </Badge>
                    <Badge className={TASK_STATUS_COLORS[task.status]}>
                      {task.status}
                    </Badge>
                    {task.due_date && (
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Vence</p>
                        <p className="font-semibold text-xs">
                          {new Date(task.due_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {hasPermission(['Admin', 'Manager', 'Agente']) && (
                      <Select
                        value={task.status}
                        onValueChange={(newStatus) => updateTaskStatus(task.id, newStatus)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TASK_STATUSES.map(status => (
                            <SelectItem key={status} value={status}>{status}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // NEW: Commission System Components
  const CommissionsView = () => {
    const [activeCommissionTab, setActiveCommissionTab] = useState('dashboard');

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-mg-green">Módulo de Comisiones</h1>
            <p className="text-gray-600 mt-2">Sistema flexible de gestión de comisiones</p>
          </div>
        </div>

        {/* Commission Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveCommissionTab('dashboard')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeCommissionTab === 'dashboard'
                  ? 'border-mg-green text-mg-green'
                  : 'border-transparent text-gray-500 hover:text-mg-green hover:border-mg-green/50'
              }`}
            >
              <PieChart className="h-4 w-4 mr-2 inline" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveCommissionTab('commissions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeCommissionTab === 'commissions'
                  ? 'border-mg-green text-mg-green'
                  : 'border-transparent text-gray-500 hover:text-mg-green hover:border-mg-green/50'
              }`}
            >
              <DollarSign className="h-4 w-4 mr-2 inline" />
              Comisiones
            </button>
            {hasPermission(['Admin', 'Manager']) && (
              <button
                onClick={() => setActiveCommissionTab('periods')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeCommissionTab === 'periods'
                    ? 'border-mg-green text-mg-green'
                    : 'border-transparent text-gray-500 hover:text-mg-green hover:border-mg-green/50'
                }`}
              >
                <Calendar className="h-4 w-4 mr-2 inline" />
                Períodos
              </button>
            )}
            {hasPermission(['Admin']) && (
              <button
                onClick={() => setActiveCommissionTab('schemes')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeCommissionTab === 'schemes'
                    ? 'border-mg-green text-mg-green'
                    : 'border-transparent text-gray-500 hover:text-mg-green hover:border-mg-green/50'
                }`}
              >
                <Settings className="h-4 w-4 mr-2 inline" />
                Configuración
              </button>
            )}
          </nav>
        </div>

        {/* Commission Tab Content */}
        {activeCommissionTab === 'dashboard' && <CommissionDashboard />}
        {activeCommissionTab === 'commissions' && <CommissionsList />}
        {activeCommissionTab === 'periods' && hasPermission(['Admin', 'Manager']) && <CommissionPeriods />}
        {activeCommissionTab === 'schemes' && hasPermission(['Admin']) && <CommissionSchemes />}
      </div>
    );
  };

  const CommissionDashboard = () => (
    <div className="space-y-6">
      {/* Dashboard Stats */}
      {commissionDashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-mg-green">
                  {commissionDashboard.current_month_commissions}
                </div>
                <div className="text-sm text-gray-600">Comisiones este mes</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-mg-green">
                  ${commissionDashboard.current_month_total?.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Total del mes</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-500">
                  {commissionDashboard.pending_commissions}
                </div>
                <div className="text-sm text-gray-600">Pendientes</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-500">
                  {commissionDashboard.approved_commissions}
                </div>
                <div className="text-sm text-gray-600">Aprobadas</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      {commissionDashboard && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-mg-green">Comisiones por Producto</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(commissionDashboard.commissions_by_product || {}).map(([product, amount]) => (
                  <div key={product} className="flex justify-between items-center">
                    <span className="text-sm font-medium">{product}</span>
                    <span className="text-sm text-mg-green font-semibold">
                      ${amount.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-mg-green">Comisiones por Estado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(commissionDashboard.commissions_by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex justify-between items-center">
                    <Badge className={COMMISSION_STATUS_COLORS[status]}>
                      {status}
                    </Badge>
                    <span className="text-sm font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  const CommissionsList = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-mg-green">Mis Comisiones</h2>
        {hasPermission(['Admin', 'Manager']) && (
          <Button
            onClick={() => {
              setCommissionAdjustment({ ...commissionAdjustment, commission_id: '' });
              setShowCommissionAdjustmentDialog(true);
            }}
            variant="outline"
            className="border-mg-green text-mg-green hover:bg-mg-green hover:text-white"
          >
            <TrendingDown className="h-4 w-4 mr-2" />
            Crear Ajuste
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prima
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Comisión
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  {hasPermission(['Admin', 'Manager']) && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {commissions.length === 0 ? (
                  <tr>
                    <td colSpan={hasPermission(['Admin', 'Manager']) ? 7 : 6} className="px-6 py-12 text-center text-gray-500">
                      <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No hay comisiones registradas</p>
                    </td>
                  </tr>
                ) : (
                  commissions.map((commission) => (
                    <tr key={commission.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {commission.client_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          {getProductIcon(commission.product_type)}
                          <span className="ml-2">{commission.product_type}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        ${commission.annual_premium?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-mg-green">
                        ${commission.commission_amount?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {hasPermission(['Admin', 'Manager']) ? (
                          <Select
                            value={commission.status}
                            onValueChange={(value) => updateCommissionStatus(commission.id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {COMMISSION_STATUSES.map(status => (
                                <SelectItem key={status} value={status}>{status}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={COMMISSION_STATUS_COLORS[commission.status]}>
                            {commission.status}
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(commission.calculated_at).toLocaleDateString()}
                      </td>
                      {hasPermission(['Admin', 'Manager']) && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <Button
                            onClick={() => {
                              setCommissionAdjustment({ 
                                ...commissionAdjustment, 
                                commission_id: commission.id 
                              });
                              setShowCommissionAdjustmentDialog(true);
                            }}
                            variant="outline"
                            size="sm"
                          >
                            Ajustar
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const CommissionPeriods = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-mg-green">Períodos de Comisión</h2>
        {hasPermission(['Admin', 'Manager']) && (
          <Button
            onClick={() => setShowCommissionPeriodDialog(true)}
            className="bg-mg-green hover:bg-mg-green/90 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Período
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {commissionPeriods.map((period) => (
          <Card key={period.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-mg-green">{period.name}</CardTitle>
                  <p className="text-sm text-gray-600">{period.period_type}</p>
                </div>
                <Badge className={COMMISSION_PERIOD_STATUS_COLORS[period.status]}>
                  {period.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Período:</span>
                  <span>{new Date(period.start_date).toLocaleDateString()} - {new Date(period.end_date).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Comisiones:</span>
                  <span className="font-semibold text-mg-green">${period.total_commissions?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Pólizas:</span>
                  <span>{period.total_policies}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Prima Total:</span>
                  <span>${period.total_premium?.toLocaleString()}</span>
                </div>
                {period.status === 'Open' && hasPermission(['Admin', 'Manager']) && (
                  <Button
                    onClick={() => calculateCommissionsForPeriod(period.id)}
                    className="w-full bg-mg-green hover:bg-mg-green/90 text-white"
                    disabled={loading}
                  >
                    <Calculator className="h-4 w-4 mr-2" />
                    Calcular Comisiones
                  </Button>
                )}
                {period.status === 'Calculated' && hasPermission(['Admin', 'Manager']) && (
                  <Select
                    value={period.status}
                    onValueChange={(value) => updateCommissionPeriodStatus(period.id, value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMISSION_PERIOD_STATUSES.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        
        {commissionPeriods.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No hay períodos de comisión registrados</p>
          </div>
        )}
      </div>
    </div>
  );

  const CommissionSchemes = () => {
    const [selectedScheme, setSelectedScheme] = useState(null);

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-mg-green">Esquemas de Comisión</h2>
          <Button
            onClick={() => setShowCommissionSchemeDialog(true)}
            className="bg-mg-green hover:bg-mg-green/90 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Esquema
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Schemes List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-mg-green">Esquemas Disponibles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {commissionSchemes.map((scheme) => (
                  <div
                    key={scheme.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedScheme?.id === scheme.id
                        ? 'border-mg-green bg-mg-green/5'
                        : 'border-gray-200 hover:border-mg-green/50'
                    }`}
                    onClick={() => {
                      setSelectedScheme(scheme);
                      fetchCommissionRules(scheme.id);
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-mg-green">{scheme.name}</h3>
                        <p className="text-sm text-gray-600">{scheme.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {scheme.period_type} • {scheme.applies_to_roles.join(', ')}
                        </p>
                      </div>
                      <Badge className={scheme.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {scheme.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                  </div>
                ))}
                
                {commissionSchemes.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No hay esquemas configurados</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Rules for Selected Scheme */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-mg-green">
                  {selectedScheme ? `Reglas: ${selectedScheme.name}` : 'Reglas de Comisión'}
                </CardTitle>
                {selectedScheme && (
                  <Button
                    onClick={() => {
                      setShowCommissionRuleDialog(true);
                    }}
                    variant="outline"
                    size="sm"
                    className="border-mg-green text-mg-green hover:bg-mg-green hover:text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nueva Regla
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {selectedScheme ? (
                <div className="space-y-4">
                  {commissionRules.map((rule) => (
                    <div key={rule.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-mg-green">{rule.name}</h4>
                          <p className="text-sm text-gray-600">{rule.description}</p>
                          
                          <div className="mt-2 space-y-1 text-xs text-gray-500">
                            <div>Tipo: {rule.commission_type}</div>
                            <div>Base: {rule.calculation_base}</div>
                            {rule.percentage_rate && <div>Tasa: {rule.percentage_rate}%</div>}
                            {rule.fixed_amount && <div>Monto: ${rule.fixed_amount}</div>}
                            <div>Prioridad: {rule.priority}</div>
                          </div>
                        </div>
                        <Badge className={rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {rule.is_active ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  
                  {commissionRules.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No hay reglas definidas</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Selecciona un esquema para ver sus reglas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Commission Dialog Components
  const CommissionSchemeDialog = () => (
    <Dialog open={showCommissionSchemeDialog} onOpenChange={setShowCommissionSchemeDialog}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-mg-green">Crear Esquema de Comisión</DialogTitle>
          <DialogDescription>
            Configure un nuevo esquema de comisiones para el sistema.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={createCommissionScheme} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="scheme_name">Nombre del Esquema*</Label>
              <Input
                id="scheme_name"
                value={newCommissionScheme.name}
                onChange={(e) => setNewCommissionScheme({ ...newCommissionScheme, name: e.target.value })}
                required
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="scheme_description">Descripción</Label>
              <Textarea
                id="scheme_description"
                value={newCommissionScheme.description}
                onChange={(e) => setNewCommissionScheme({ ...newCommissionScheme, description: e.target.value })}
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="period_type">Tipo de Período</Label>
              <Select
                value={newCommissionScheme.period_type}
                onValueChange={(value) => setNewCommissionScheme({ ...newCommissionScheme, period_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMISSION_PERIOD_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Roles Aplicables</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {USER_ROLES.filter(role => ['Admin', 'Manager', 'Agente'].includes(role)).map(role => (
                <div key={role} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`scheme_role_${role}`}
                    checked={newCommissionScheme.applies_to_roles.includes(role)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewCommissionScheme({
                          ...newCommissionScheme,
                          applies_to_roles: [...newCommissionScheme.applies_to_roles, role]
                        });
                      } else {
                        setNewCommissionScheme({
                          ...newCommissionScheme,
                          applies_to_roles: newCommissionScheme.applies_to_roles.filter(r => r !== role)
                        });
                      }
                    }}
                  />
                  <Label htmlFor={`scheme_role_${role}`}>{role}</Label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Productos Aplicables</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {getTranslatedProductTypes().map((product, index) => (
                <div key={PRODUCT_TYPES[index]} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`scheme_product_${PRODUCT_TYPES[index]}`}
                    checked={newCommissionScheme.applies_to_products.includes(PRODUCT_TYPES[index])}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewCommissionScheme({
                          ...newCommissionScheme,
                          applies_to_products: [...newCommissionScheme.applies_to_products, PRODUCT_TYPES[index]]
                        });
                      } else {
                        setNewCommissionScheme({
                          ...newCommissionScheme,
                          applies_to_products: newCommissionScheme.applies_to_products.filter(p => p !== PRODUCT_TYPES[index])
                        });
                      }
                    }}
                  />
                  <Label htmlFor={`scheme_product_${PRODUCT_TYPES[index]}`}>{product}</Label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCommissionSchemeDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-mg-green hover:bg-mg-green/90 text-white"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Esquema'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  // NEW: Communication Hub Dialogs - Define before main return to avoid initialization errors
  const TemplateDialog = () => {
    const [formData, setFormData] = useState({
      name: selectedTemplate?.name || '',
      description: selectedTemplate?.description || '',
      template_type: selectedTemplate?.template_type || '',
      communication_type: selectedTemplate?.communication_type || 'Email',
      subject: selectedTemplate?.subject || '',
      content: selectedTemplate?.content || '',
      variables: selectedTemplate?.variables?.join(', ') || '',
      language: selectedTemplate?.language || 'es'
    });

    // Stable callback for template form to prevent input focus loss
    const handleTemplateFieldChange = useCallback((field, value) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
        const templateData = {
          ...formData,
          variables: formData.variables.split(',').map(v => v.trim()).filter(v => v)
        };
        
        if (selectedTemplate) {
          await updateCommunicationTemplate(selectedTemplate.id, templateData);
        } else {
          await createCommunicationTemplate(templateData);
        }
      } catch (error) {
        console.error('Error saving template:', error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedTemplate ? 'Editar Plantilla' : 'Nueva Plantilla de Comunicación'}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate ? 'Modifica la información de la plantilla' : 'Crea una nueva plantilla para emails o SMS'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nombre de la Plantilla</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleTemplateFieldChange('name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="communication_type">Tipo de Comunicación</Label>
                <Select
                  value={formData.communication_type}
                  onValueChange={(value) => setFormData({...formData, communication_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="SMS">SMS</SelectItem>
                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="template_type">Tipo de Plantilla</Label>
                <Select
                  value={formData.template_type}
                  onValueChange={(value) => setFormData({...formData, template_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Lead Welcome">Bienvenida de Lead</SelectItem>
                    <SelectItem value="Quote Generated">Cotización Generada</SelectItem>
                    <SelectItem value="Quote Reminder">Recordatorio de Cotización</SelectItem>
                    <SelectItem value="Policy Confirmation">Confirmación de Póliza</SelectItem>
                    <SelectItem value="Renewal Reminder">Recordatorio de Renovación</SelectItem>
                    <SelectItem value="Task Assignment">Asignación de Tarea</SelectItem>
                    <SelectItem value="Custom">Personalizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="language">Idioma</Label>
                <Select
                  value={formData.language}
                  onValueChange={(value) => setFormData({...formData, language: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleTemplateFieldChange('description', e.target.value)}
                placeholder="Descripción opcional"
              />
            </div>

            {formData.communication_type === 'Email' && (
              <div>
                <Label htmlFor="subject">Asunto del Email</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => handleTemplateFieldChange('subject', e.target.value)}
                  placeholder="Asunto del email (puede incluir variables como {recipient_name})"
                />
              </div>
            )}

            <div>
              <Label htmlFor="content">Contenido de la Plantilla</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                rows={8}
                placeholder="Contenido de la plantilla. Usa variables como {recipient_name}, {company_name}, etc."
                required
              />
            </div>

            <div>
              <Label htmlFor="variables">Variables Disponibles</Label>
              <Input
                id="variables"
                value={formData.variables}
                onChange={(e) => setFormData({...formData, variables: e.target.value})}
                placeholder="recipient_name, company_name, lead_product_type (separadas por comas)"
              />
              <div className="text-xs text-gray-500 mt-1">
                Variables comunes: recipient_name, company_name, current_date, lead_product_type, quote_amount, policy_number
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowTemplateDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="bg-mg-green hover:bg-mg-green/90">
                {loading ? 'Guardando...' : selectedTemplate ? 'Actualizar' : 'Crear Plantilla'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  const TestCommunicationDialog = () => {
    const [testData, setTestData] = useState({
      test_recipient_email: '',
      test_recipient_phone: '',
      variables: {}
    });

    const handleTest = async () => {
      if (!selectedTemplate) return;
      
      setLoading(true);
      try {
        const result = await testCommunication({
          communication_type: selectedTemplate.communication_type,
          template_id: selectedTemplate.id,
          test_recipient_email: testData.test_recipient_email,
          test_recipient_phone: testData.test_recipient_phone,
          variables: testData.variables
        });
        
        if (result.success) {
          alert('Mensaje de prueba enviado correctamente');
          setShowTestCommunicationDialog(false);
        } else {
          alert(`Error: ${result.error}`);
        }
      } catch (error) {
        alert('Error enviando mensaje de prueba');
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <Dialog open={showTestCommunicationDialog} onOpenChange={setShowTestCommunicationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Probar Plantilla: {selectedTemplate?.name}</DialogTitle>
            <DialogDescription>
              Envía un mensaje de prueba usando esta plantilla
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedTemplate?.communication_type === 'Email' ? (
              <div>
                <Label htmlFor="test_email">Email de Prueba</Label>
                <Input
                  id="test_email"
                  type="email"
                  value={testData.test_recipient_email}
                  onChange={(e) => setTestData({...testData, test_recipient_email: e.target.value})}
                  placeholder="tu@email.com"
                  required
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="test_phone">Teléfono de Prueba</Label>
                <Input
                  id="test_phone"
                  value={testData.test_recipient_phone}
                  onChange={(e) => setTestData({...testData, test_recipient_phone: e.target.value})}
                  placeholder="+1234567890"
                  required
                />
              </div>
            )}
            
            <div>
              <Label>Variables de Prueba (JSON)</Label>
              <Textarea
                value={JSON.stringify(testData.variables, null, 2)}
                onChange={(e) => {
                  try {
                    setTestData({...testData, variables: JSON.parse(e.target.value)});
                  } catch (error) {
                    // Invalid JSON, keep current state
                  }
                }}
                placeholder={`{"recipient_name": "Juan Pérez", "company_name": "${COMPANY_CONFIG.name}"}`}
                rows={4}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowTestCommunicationDialog(false)}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleTest} 
                disabled={loading} 
                className="bg-mg-green hover:bg-mg-green/90"
              >
                {loading ? 'Enviando...' : 'Enviar Prueba'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // NEW: Super Admin View Component - MOVED BEFORE MAIN RETURN TO FIX INITIALIZATION ERROR
  const SuperAdminView = () => {
    const [loading, setLoading] = useState(false);
    
    useEffect(() => {
      if (activeSuperAdminTab === 'dashboard') {
        fetchSuperAdminDashboard();
      } else if (activeSuperAdminTab === 'tenants') {
        fetchTenants(tenantFilters);
      } else if (activeSuperAdminTab === 'users') {
        fetchAllSystemUsers(userFilters);
      }
    }, [activeSuperAdminTab, tenantFilters, userFilters]);

    const handleTenantSubmit = async (tenantData) => {
      if (selectedTenant) {
        await updateTenant(selectedTenant.id, tenantData);
      } else {
        await createTenant(tenantData);
      }
    };

    const handleTenantEdit = (tenant) => {
      setSelectedTenant(tenant);
      setShowTenantDialog(true);
    };

    const handleTenantView = (tenant) => {
      setSelectedTenant(tenant);
      setActiveSuperAdminTab('tenantDetails');
    };

    const handleManageBranding = async (tenant) => {
      setSelectedTenant(tenant);
      await fetchTenantBranding(tenant.id);
      setActiveSuperAdminTab('branding');
    };

    const handleBrandingUpdate = async (brandingData) => {
      if (selectedTenant) {
        await updateTenantBranding(selectedTenant.id, brandingData);
      }
    };

    const handlePreviewBranding = (brandingConfig) => {
      // This would open a preview window/modal with the branding applied
      alert('Branding preview functionality would open here');
    };

    return (
      <div className="space-y-6">
        <Tabs value={activeSuperAdminTab} onValueChange={setActiveSuperAdminTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="dashboard" className="flex items-center">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="tenants" className="flex items-center">
              <Building className="h-4 w-4 mr-2" />
              Tenants
            </TabsTrigger>
            <TabsTrigger value="branding" className="flex items-center">
              <Palette className="h-4 w-4 mr-2" />
              White Label
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center">
              <Users className="h-4 w-4 mr-2" />
              System Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            <SuperAdminDashboard
              dashboardStats={superAdminDashboard}
              onRefresh={fetchSuperAdminDashboard}
              onNavigateToTenants={() => setActiveSuperAdminTab('tenants')}
              onNavigateToUsers={() => setActiveSuperAdminTab('users')}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="tenants" className="mt-6">
            <TenantManagement
              tenants={tenants}
              onCreateTenant={() => {
                setSelectedTenant(null);
                setShowTenantDialog(true);
              }}
              onEditTenant={handleTenantEdit}
              onDeleteTenant={(tenant) => deleteTenant(tenant.id)}
              onViewTenant={handleTenantView}
              onManageBranding={handleManageBranding}
              loading={loading}
              searchTerm={tenantFilters.search}
              onSearchChange={(search) => setTenantFilters(prev => ({...prev, search}))}
              statusFilter={tenantFilters.status}
              onStatusFilterChange={(status) => setTenantFilters(prev => ({...prev, status}))}
              planFilter={tenantFilters.plan}
              onPlanFilterChange={(plan) => setTenantFilters(prev => ({...prev, plan}))}
            />
          </TabsContent>

          <TabsContent value="branding" className="mt-6">
            <WhiteLabelConfig
              tenants={tenants}
              selectedTenant={selectedTenant}
              onTenantSelect={async (tenantId) => {
                const tenant = tenants.find(t => t.id === tenantId);
                setSelectedTenant(tenant);
                if (tenant) {
                  await fetchTenantBranding(tenantId);
                }
              }}
              brandingConfig={tenantBrandingConfig}
              onUpdateBranding={handleBrandingUpdate}
              onPreviewBranding={handlePreviewBranding}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <SystemUsersManagement
              users={allSystemUsers}
              tenants={tenants}
              onCreateUser={() => {
                // TODO: Open user creation dialog
                alert('User creation dialog would open here');
              }}
              onEditUser={(user) => {
                // TODO: Open user edit dialog
                alert('User edit dialog would open here');
              }}
              onDeleteUser={(user) => {
                // TODO: Implement user deletion
                alert('User deletion would be implemented here');
              }}
              loading={loading}
              searchTerm={userFilters.search}
              onSearchChange={(search) => setUserFilters(prev => ({...prev, search}))}
              roleFilter={userFilters.role}
              onRoleFilterChange={(role) => setUserFilters(prev => ({...prev, role}))}
              tenantFilter={userFilters.tenant}
              onTenantFilterChange={(tenant) => setUserFilters(prev => ({...prev, tenant}))}
            />
          </TabsContent>
        </Tabs>

        {/* Tenant Form Dialog */}
        <Dialog open={showTenantDialog} onOpenChange={setShowTenantDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedTenant ? 'Edit Tenant' : 'Create New Tenant'}
              </DialogTitle>
              <DialogDescription>
                {selectedTenant 
                  ? 'Update tenant information and configuration.'
                  : 'Create a new tenant organization with custom branding and configuration.'
                }
              </DialogDescription>
            </DialogHeader>
            <TenantForm
              tenant={selectedTenant}
              onSubmit={handleTenantSubmit}
              onCancel={() => {
                setShowTenantDialog(false);
                setSelectedTenant(null);
              }}
              loading={loading}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // First, handle landing page and authentication routing
  if (!isAuthenticated) {
    if (currentPage === 'privacy') {
      return <PrivacyPolicy onBackClick={handleBackToLanding} />;
    }
    
    if (currentPage === 'terms') {
      return <TermsOfService onBackClick={handleBackToLanding} />;
    }
    
    if (showLandingPage && currentPage === 'landing') {
      return (
        <LandingPage 
          onLoginClick={handleLoginClick}
          onSignUpClick={handleSignUpClick}
        />
      );
    }
    
    // Show authentication handler for login/signup
    return (
      <AuthHandler 
        onAuthSuccess={(user) => {
          setCurrentUser(user);
          setIsAuthenticated(true);
          setShowLandingPage(false);
          setCurrentPage('app');
        }}
        onBackToLanding={handleBackToLanding}
        onShowPrivacyPolicy={handleShowPrivacyPolicy}
        onShowTermsOfService={handleShowTermsOfService}
      />
    );
  }

  // Main authenticated app interface
  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar Navigation */}
      <SidebarNavigation
        activeView={activeView}
        setActiveView={setActiveView}
        hasPermission={hasPermission}
        currentUser={currentUser}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        className="fixed h-full z-30"
      />

      {/* Main Content Area */}
      <div className={`flex-1 transition-all duration-300 ${
        sidebarCollapsed ? 'ml-16' : 'ml-64'
      }`}>
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Left side - Page Title */}
              <div className="flex items-center">
                <h1 className="text-xl font-semibold text-gray-900 capitalize">
                  {activeView === 'dashboard' ? t('navigation.dashboard') :
                   activeView === 'leads' ? t('navigation.leads') :
                   activeView === 'quotes' ? t('navigation.quotes') :
                   activeView === 'policies' ? t('navigation.policies') :
                   activeView === 'users' ? t('navigation.users') :
                   activeView === 'renewals' ? t('navigation.renewals') :
                   activeView === 'tasks' ? t('navigation.tasks') :
                   activeView === 'commissions' ? t('navigation.commissions') :
                   activeView === 'integrations' ? t('navigation.integrations') :
                   activeView === 'communications' ? t('navigation.communications') :
                   activeView === 'analytics' ? t('navigation.analytics') :
                   activeView === 'superadmin' ? t('navigation.superAdmin') :
                   activeView}
                </h1>
              </div>

              {/* Right side - User menu and language selector */}
              <div className="flex items-center space-x-4">
                <LanguageSelector />
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center space-x-2">
                      <div className="bg-mg-green text-white p-1.5 rounded-full text-sm font-semibold">
                        {currentUser?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <span className="text-sm font-medium">
                        {currentUser?.full_name || currentUser?.username}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>{t('users.profile.title')}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={openProfileDialog}>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>{t('users.profile.updateProfile')}</span>
                    </DropdownMenuItem>
                    {hasPermission(['Admin', 'Super Admin']) && (
                      <DropdownMenuItem onClick={() => setShowBrandingConfig(true)}>
                        <Palette className="mr-2 h-4 w-4" />
                        <span>Configurar Marca</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>{t('auth.logout')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-4 sm:px-6 lg:px-8 py-8 max-w-full">
          {activeView === 'dashboard' && <Dashboard />}
          {activeView === 'leads' && hasPermission(['Admin', 'Manager', 'Agente', 'CSR']) && <LeadsPipeline />}
          {activeView === 'quotes' && hasPermission(['Admin', 'Manager', 'Agente', 'CSR']) && <QuotesView />}
          {activeView === 'policies' && hasPermission(['Admin', 'Manager', 'Agente', 'CSR']) && <PoliciesView />}
          {activeView === 'users' && hasPermission(['Admin']) && <UsersView />}
          {activeView === 'renewals' && hasPermission(['Admin', 'Manager', 'Agente', 'CSR']) && <RenewalsView />}
          {activeView === 'tasks' && hasPermission(['Admin', 'Manager', 'Agente', 'CSR']) && <TasksView />}
          {activeView === 'commissions' && hasPermission(['Admin', 'Manager', 'Agente']) && <CommissionsView />}
          {activeView === 'integrations' && hasPermission(['Admin', 'Manager']) && <IntegrationsViewWrapper />}
          {activeView === 'communications' && hasPermission(['Admin', 'Manager', 'Agente', 'CSR']) && <CommunicationsView />}
          {activeView === 'analytics' && hasPermission(['Admin', 'Manager']) && (
            <AnalyticsView
              analyticsDashboard={analyticsDashboard}
              timeBasedMetrics={timeBasedMetrics}
              agentPerformance={agentPerformance}
              conversionFunnel={conversionFunnel}
              revenueAnalytics={revenueAnalytics}
              isLoading={isAnalyticsLoading}
              onRefresh={refreshAnalytics}
              onDateRangeChange={setAnalyticsDateRange}
              onGenerateReport={generateCustomReport}
              onExport={exportAnalyticsData}
              dateRange={analyticsDateRange}
              selectedPeriod={selectedPeriod}
            />
          )}
          {activeView === 'superadmin' && hasPermission(['Super Admin']) && <SuperAdminView />}
        </main>
      </div>

      {/* Dialogs */}
      <QuoteDialog />
      <ProfileDialog />
      <RenewalConfigDialog />
      <TaskDialog />
      <CommissionSchemeDialog />
      <CommissionRuleDialog />
      <CommissionPeriodDialog />
      <CommissionAdjustmentDialog />
      <IntegrationDialog />
      <FieldMappingDialog />
      <TestIntegrationDialog />
      <TemplateDialog />
      <TestCommunicationDialog />
      <BrandingConfig 
        isOpen={showBrandingConfig} 
        onClose={() => setShowBrandingConfig(false)}
        currentUser={currentUser}
      />
    </div>
  );

}

export default App;
